import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useWorkspace } from "../../contexts/Workspacecontext";
import {
  useRolesAndDepartments,
  useApprovedVacancies,
  useHiringFlows,
} from "../../hooks/useCommonData";
import "./JobPosts.scss";
import WarningPopup from "../../components/WarningPopup/WarningPopup";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import QRShareModal from "../../components/QrShareModal/QRShareModal";
import IconTooltip from "../Vacancies/IconTooltip";
import LockedButton from "../../components/LockedButton";
import { RECRUITMENT, CONSULTANCY } from "../../config/moduleAccess";

// ─── API URLs ─────────────────────────────────────────────────────────────────
const COMPANY_API = `${process.env.REACT_APP_API_HR_URL}/recruiter/recruitment/jobposts`;
const CONSULTANT_API = `${process.env.REACT_APP_API_CONSULTANT_URL}/consultancy/jobpost`;
const CONSULTANT_REQ = `${process.env.REACT_APP_API_CONSULTANT_URL}/consultancy/candidate-requirement`;
const COMMON = process.env.REACT_APP_COMMON_API_URL;
const CONNECTIONS_API = `${process.env.REACT_APP_API_HR_URL}/connections/consultancy`;
const BRANCHES_API = `${process.env.REACT_APP_COMMON_API_URL}/branches/list`;

const INTERVIEW_MODES = [
  ["ONLINE", "Online"],
  ["OFFLINE", "Offline"],
  ["HYBRID", "Hybrid"],
];

const BUDGET_TYPES = [
  ["ANNUAL", "Annual"],
  ["MONTHLY", "Monthly"],
];

const SALARY_TYPES = [
  ["CTC", "CTC"],
  ["GROSS", "Gross"],
  ["NET", "Net"],
];

const JOB_DESCRIPTION_SUGGESTIONS = [
  "Handle day-to-day job responsibilities and assigned tasks",
  "Coordinate with internal teams and external stakeholders",
  "Ensure process compliance and operational efficiency",
  "Support department and business objectives",
  "Maintain accurate records and reporting",
];

const DESIRED_PROFILE_SUGGESTIONS = [
  "Strong communication and interpersonal skills",
  "Ability to work independently and collaboratively",
  "Good organizational and time management abilities",
  "Problem-solving mindset with attention to detail",
  "Adaptable and willing to learn new processes",
];

const RESPONSIBILITY_SUGGESTIONS = [
  "Manage assigned responsibilities and daily operations",
  "Coordinate with teams for smooth workflow execution",
  "Maintain documentation and process records",
  "Ensure timely completion of assigned tasks",
  "Support organizational and department goals",
  "Monitor work quality and compliance standards",
  "Handle stakeholder communication effectively",
  "Identify and resolve operational issues",
];

const EXPERIENCE_SUGGESTIONS = [
  "Fresher (0 years)",
  "0–1 years",
  "1–2 years",
  "2–3 years",
  "3–5 years",
  "5–7 years",
  "7–10 years",
  "10+ years",
  "Experience in recruitment industry",
  "Experience in client handling",
  "Experience in team management",
  "Experience in sales and business development",
];

const normalizeEmploymentType = (value = "") =>
  value ? value.toUpperCase().replace(/[\s-]/g, "_") : "";

const normalizeConnectionStatus = (value = "") =>
  value ? value.toUpperCase().replace(/[\s-]/g, "_") : "";

// ─── Indian currency formatting ───────────────────────────────────────────────
const toIndianFormat = (num) => {
  if (!num && num !== 0) return "";
  const str = String(Math.floor(Number(num)));
  if (str.length <= 3) return str;
  const last3 = str.slice(-3);
  const rest = str.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
};

const fromIndianFormat = (str) => str.replace(/,/g, "");

const getNormalizedStatus = (status = "") =>
  String(status).trim().toUpperCase();

const isClosingSoon = (value) => {
  if (!value) return false;
  const closingDate = new Date(value);
  if (Number.isNaN(closingDate.getTime())) return false;
  const today = new Date();
  const windowEnd = new Date();
  windowEnd.setDate(today.getDate() + 14);
  return closingDate >= today && closingDate <= windowEnd;
};

const countByStatus = (posts, status) =>
  posts.filter((post) => getNormalizedStatus(post.status) === status).length;

// Falls back to expectedClosingTime for older payloads that predate
// applicationClosingDate.
const isPastApplicationClosingDate = (post) => {
  const value = post?.applicationClosingDate || post?.expectedClosingTime;
  if (!value) return false;
  const closingDate = new Date(value);
  if (Number.isNaN(closingDate.getTime())) return false;
  return closingDate.getTime() < Date.now();
};

// ─── Qualification IDs from response DTO ─────────────────────────────────────
// Response DTOs may return qualifications as an array of objects or as legacy
// qualificationIds array. This helper normalises to an array of IDs.
const extractQualificationIds = (post) => {
  if (Array.isArray(post.qualifications) && post.qualifications.length > 0) {
    return post.qualifications.map((q) =>
      typeof q === "object" ? q.qualificationId : q,
    );
  }
  if (Array.isArray(post.qualificationIds)) return post.qualificationIds;
  return [];
};

// ─── useQualificationsLocal ───────────────────────────────────────────────────
const useQualifications = () => {
  const [qualifications, setQualifications] = useState([]);
  const [qualificationsLoading, setQualificationsLoading] = useState(true);
  const { api, isConsultant } = useWorkspace();

  useEffect(() => {
    const url = isConsultant
      ? `${api.base}/job-post-qualification/all` // → /api/consultant/job-post-qualification/all
      : `${api.base}/job-post-qualification/all`; // → /api/recruiter-hr/recruiter/job-post-qualification/all

    axios
      .get(url)
      .then((r) => setQualifications(r.data.data || []))
      .catch(console.error)
      .finally(() => setQualificationsLoading(false));
  }, [api.base, isConsultant]);

  return { qualifications, qualificationsLoading };
};
// ─── QualificationMultiSelect ─────────────────────────────────────────────────
// Replaces the old two-dropdown (type + qualification) pattern.
// Renders grouped checkboxes by qualificationType, supports search, shows chips.
const QualificationMultiSelect = ({
  qualifications,
  qualificationsLoading,
  selectedIds,
  onChange,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return qualifications.filter(
      (item) =>
        item.qualificationName.toLowerCase().includes(q) ||
        item.qualificationType.toLowerCase().includes(q),
    );
  }, [qualifications, search]);

  // Group by type
  const grouped = useMemo(() => {
    return filtered.reduce((acc, item) => {
      const t = item.qualificationType;
      if (!acc[t]) acc[t] = [];
      acc[t].push(item);
      return acc;
    }, {});
  }, [filtered]);

  const selectedItems = qualifications.filter((q) =>
    selectedIds.includes(q.qualificationId),
  );

  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const FRIENDLY_TYPE = {
    SCHOOL: "School",
    PRE_UNIVERSITY: "Pre-University",
    BACHELOR: "Bachelor's Degree",
    MASTER: "Master's Degree",
    PHD: "PhD / Doctorate",
    OTHER: "Other",
  };

  return (
    <div
      ref={containerRef}
      style={{ position: "relative" }}
      className={error ? "jp-qs--error" : ""}
    >
      {/* Trigger button */}
      <button
        type="button"
        className={`jp-input${error ? " jp-input--error" : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 38,
          height: "auto",
          padding: "6px 10px",
          flexWrap: "wrap",
          gap: 4,
          cursor: qualificationsLoading ? "not-allowed" : "pointer",
          textAlign: "left",
        }}
        onClick={() => !qualificationsLoading && setOpen((p) => !p)}
        disabled={qualificationsLoading}
      >
        <span style={{ display: "flex", flexWrap: "wrap", gap: 4, flex: 1 }}>
          {selectedItems.length === 0 ? (
            <span style={{ color: "#9ca3af", fontSize: 13 }}>
              {qualificationsLoading
                ? "Loading qualifications…"
                : "Select qualifications"}
            </span>
          ) : (
            selectedItems.map((item) => (
              <span
                key={item.qualificationId}
                style={{
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  borderRadius: 4,
                  padding: "1px 6px",
                  fontSize: 11,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {item.qualificationName}
                <span
                  role="button"
                  style={{ cursor: "pointer", lineHeight: 1 }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    toggle(item.qualificationId);
                  }}
                >
                  ×
                </span>
              </span>
            ))
          )}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.15s",
          }}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="#6b7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {/* Search */}
          <div
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid #f3f4f6",
              position: "sticky",
              top: 0,
              background: "#fff",
            }}
          >
            <input
              autoFocus
              className="jp-input"
              style={{ fontSize: 12, padding: "4px 8px" }}
              placeholder="Search qualifications…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {Object.keys(grouped).length === 0 ? (
            <div
              style={{
                padding: 12,
                fontSize: 13,
                color: "#9ca3af",
                textAlign: "center",
              }}
            >
              No qualifications found
            </div>
          ) : (
            Object.entries(grouped).map(([type, items]) => (
              <div key={type}>
                <div
                  style={{
                    padding: "6px 12px",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    background: "#f9fafb",
                    borderBottom: "1px solid #f3f4f6",
                    position: "sticky",
                    top: 44,
                  }}
                >
                  {FRIENDLY_TYPE[type] || type}
                </div>
                {items.map((item) => {
                  const checked = selectedIds.includes(item.qualificationId);
                  return (
                    <label
                      key={item.qualificationId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "7px 12px",
                        cursor: "pointer",
                        fontSize: 13,
                        background: checked ? "#eff6ff" : "transparent",
                        borderBottom: "1px solid #f3f4f6",
                        transition: "background 0.1s",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(item.qualificationId)}
                        style={{ accentColor: "#2563eb" }}
                      />
                      <span
                        style={{ flex: 1, fontWeight: checked ? 600 : 400 }}
                      >
                        {item.qualificationName}
                      </span>
                      {checked && (
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 13 13"
                          fill="none"
                        >
                          <path
                            d="M2.5 6.5l3 3 5-5"
                            stroke="#2563eb"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </label>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}

      {error && (
        <span
          className="jp-field__error"
          style={{ marginTop: 4, display: "block" }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

// ─── Date-Time Picker ─────────────────────────────────────────────────────────
const DateTimePicker = ({ value, onChange, error, name }) => {
  const datePart = value ? value.split("T")[0] : "";
  const timePart = value ? value.split("T")[1] || "10:00" : "10:00";
  const hours = timePart.split(":")[0] || "10";
  const minutes = timePart.split(":")[1] || "00";

  const QUICK_TIMES = [
    { label: "9 AM", h: "09", m: "00" },
    { label: "10 AM", h: "10", m: "00" },
    { label: "11 AM", h: "11", m: "00" },
    { label: "12 PM", h: "12", m: "00" },
    { label: "5 PM", h: "17", m: "00" },
  ];

  const QUICK_DATES = [
    { label: "+7 days", days: 7 },
    { label: "+15 days", days: 15 },
    { label: "+30 days", days: 30 },
    { label: "+60 days", days: 60 },
  ];

  const emitChange = (d, h, m) => {
    if (!d) return;
    onChange({ target: { name, value: `${d}T${h}:${m}` } });
  };

  const applyQuickDate = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    const dateStr = d.toISOString().split("T")[0];
    emitChange(dateStr, hours, minutes);
  };

  const currentHourStr = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  const isActiveTime = (h, m) =>
    h === String(hours).padStart(2, "0") &&
    m === String(minutes).padStart(2, "0");

  return (
    <div className={`dtp-wrap${error ? " dtp-wrap--error" : ""}`}>
      <div className="dtp-row">
        <div className="dtp-date-wrap">
          <svg
            className="dtp-icon"
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
          >
            <rect
              x="1"
              y="2"
              width="13"
              height="12"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.4"
            />
            <path
              d="M1 6h13M5 1v2M10 1v2"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="date"
            className="dtp-date"
            value={datePart}
            onChange={(e) => emitChange(e.target.value, hours, minutes)}
            min={new Date().toISOString().split("T")[0]}
          />
        </div>
        <div className="dtp-time-wrap">
          <svg
            className="dtp-icon"
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
          >
            <circle
              cx="7.5"
              cy="7.5"
              r="6"
              stroke="currentColor"
              strokeWidth="1.4"
            />
            <path
              d="M7.5 4v3.5l2 2"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="time"
            className="dtp-time"
            value={currentHourStr}
            onChange={(e) => {
              const [h, m] = e.target.value.split(":");
              emitChange(datePart, h, m);
            }}
          />
        </div>
      </div>
      <div className="dtp-chips">
        {QUICK_DATES.map((q) => (
          <button
            key={q.days}
            type="button"
            className="dtp-chip"
            onClick={() => applyQuickDate(q.days)}
          >
            {q.label}
          </button>
        ))}
      </div>
      <div className="dtp-chips">
        {QUICK_TIMES.map((t) => (
          <button
            key={t.label}
            type="button"
            className={`dtp-chip${isActiveTime(t.h, t.m) ? " dtp-chip--active" : ""}`}
            onClick={() =>
              emitChange(
                datePart || new Date().toISOString().split("T")[0],
                t.h,
                t.m,
              )
            }
          >
            {t.label}
          </button>
        ))}
      </div>
      {datePart && (
        <div className="dtp-preview">
          📅{" "}
          {new Date(`${datePart}T${hours}:${minutes}`).toLocaleString("en-IN", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}
        </div>
      )}
    </div>
  );
};

const ShareMenu = ({ link, onShowQR, iconOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.top - 4, left: rect.right });
    }
    setOpen((p) => !p);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
      setOpen(false);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const menu = open
    ? createPortal(
        <div
          ref={containerRef}
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            transform: "translate(-100%, -100%)",
            background: "#fff",
            border: "0.5px solid #e5e7eb",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            minWidth: 145,
            zIndex: 9999,
            overflow: "hidden",
          }}
        >
          <button
            className="jp-share-item"
            onClick={() => {
              onShowQR();
              setOpen(false);
            }}
          >
            📷 QR Code
          </button>
          <button
            className="jp-share-item"
            onClick={() => {
              window.open(link, "_blank");
              setOpen(false);
            }}
          >
            🔍 View Link
          </button>
          <button className="jp-share-item" onClick={handleCopy}>
            📋 Copy Link
          </button>
        </div>,
        document.body,
      )
    : null;

  return (
    <div style={{ position: "relative" }}>
      {iconOnly ? (
        <button
          ref={btnRef}
          className="jd-icon-btn"
          onClick={handleOpen}
          title="Share"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "28px",
            height: "28px",
            borderRadius: "20px",
            border: "0.5px solid rgba(0, 0, 0, 0.16)",
            background: "transparent",
            cursor: "pointer",
            flexShrink: 0,
            transition: "background 0.12s, color 0.12s",
            fontSize: "13px",
          }}
        >
          🔗
        </button>
      ) : (
        <button
          ref={btnRef}
          className="jp-act-btn jp-act-btn--share"
          onClick={handleOpen}
        >
          🔗 Share <span style={{ marginLeft: 4 }}>▾</span>
        </button>
      )}
      {menu}
    </div>
  );
};

// ─── Experience Options ───────────────────────────────────────────────────────
const EXPERIENCE_OPTIONS = [
  { label: "0 yr", value: "Fresher (0 years)" },
  { label: "0–1 yr", value: "0–1 years" },
  { label: "1–2 yrs", value: "1–2 years" },
  { label: "2–3 yrs", value: "2–3 years" },
  { label: "3–5 yrs", value: "3–5 years" },
  { label: "5–7 yrs", value: "5–7 years" },
  { label: "7–10 yrs", value: "7–10 years" },
  { label: "10+ yrs", value: "10+ years" },
  { label: "Custom", value: "custom" },
];

const ExperienceCards = ({ value, onChange, name = "experience" }) => {
  const presetValues = EXPERIENCE_OPTIONS.filter(
    (o) => o.value !== "custom",
  ).map((o) => o.value);
  const selectedCard = presetValues.includes(value) ? value : "custom";
  const inputValue = value || "";

  const handleCard = (opt) => {
    if (opt.value === "custom") {
      onChange({ target: { name, value: "" } });
      return;
    }
    onChange({ target: { name, value: opt.value } });
  };

  return (
    <div className="exp-wrap">
      <div className="exp-cards">
        {EXPERIENCE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`exp-card${selectedCard === opt.value ? " exp-card--active" : ""}`}
            onClick={() => handleCard(opt)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 8 }}>
        <input
          className="jp-input"
          placeholder="Or enter custom experience requirement"
          value={inputValue}
          onFocus={() => {
            if (selectedCard !== "custom")
              onChange({ target: { name, value: "" } });
          }}
          onChange={(e) =>
            onChange({ target: { name, value: e.target.value } })
          }
        />
      </div>
    </div>
  );
};

// ─── Budget Input ─────────────────────────────────────────────────────────────
const BudgetInput = ({ value, display, budgetType, salaryType, onChange }) => {
  const handleRawChange = (e) => {
    const raw = fromIndianFormat(e.target.value).replace(/[^\d]/g, "");
    const formatted = raw ? toIndianFormat(raw) : "";
    onChange({ budget: raw, budgetDisplay: formatted });
  };

  return (
    <div className="budget-wrap">
      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        <div
          className="budget-input-row"
          style={{ flex: 1, position: "relative" }}
        >
          <span className="budget-rupee">₹</span>
          <input
            className="cv-field__input budget-input"
            value={display}
            onChange={handleRawChange}
            placeholder="e.g. 8,00,000"
            inputMode="numeric"
            style={{ paddingRight: "90px" }}
          />
          <div
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              gap: "4px",
            }}
          >
            {BUDGET_TYPES.map(([val, label]) => (
              <button
                key={val}
                type="button"
                className={`budget-type-btn${budgetType === val ? " budget-type-btn--active" : ""}`}
                onClick={() => onChange({ budgetType: val })}
                style={{ padding: "2px 6px", fontSize: "11px" }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "6px",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontSize: 12, color: "#6b7280" }}>Salary type:</span>
          {SALARY_TYPES.map(([val, label]) => (
            <button
              key={val}
              type="button"
              className={`budget-type-btn${salaryType === val ? " budget-type-btn--active" : ""}`}
              onClick={() => onChange({ salaryType: val })}
              style={{ padding: "4px 8px", fontSize: "12px" }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {display && (
        <div className="budget-preview">
          ₹{display}{" "}
          {BUDGET_TYPES.find((b) => b[0] === budgetType)?.[1].toLowerCase() ||
            ""}
          {" · "}
          {SALARY_TYPES.find((s) => s[0] === salaryType)?.[1] || ""}
          {budgetType === "MONTHLY" && value
            ? ` (₹${toIndianFormat(Number(value) * 12)} annual)`
            : ""}
        </div>
      )}
    </div>
  );
};

// ─── Shared UI primitives ─────────────────────────────────────────────────────
const F = ({ label, required, error, hint, children }) => (
  <div className={`jp-field${error ? " jp-field--error" : ""}`}>
    <label className="jp-field__label">
      {label}
      {required && <span className="jp-field__req"> *</span>}
    </label>
    {children}
    {hint && !error && <span className="jp-field__hint">{hint}</span>}
    {error && <span className="jp-field__error">{error}</span>}
  </div>
);

const Toggle = ({ label, name, checked, onChange, icon }) => (
  <label className="jp-toggle">
    <input type="checkbox" name={name} checked={checked} onChange={onChange} />
    <span className="jp-toggle__track">
      <span className="jp-toggle__thumb" />
    </span>
    <span className="jp-toggle__label">
      {icon} {label}
    </span>
  </label>
);

const StatusBadge = ({ status }) => {
  const normalized = (status || "").toUpperCase();
  const config = {
    OPEN: { className: "badge badge--open", label: "Open", dot: "🟢" },
    CLOSED: { className: "badge badge--closed", label: "Closed", dot: "⚫" },
    DRAFT: { className: "badge badge--draft", label: "Draft", dot: "🟡" },
    CANCELLED: {
      className: "badge badge--cancelled",
      label: "Cancelled",
      dot: "🔴",
    },
  };
  const current = config[normalized] || {
    className: "badge badge--default",
    label: status || "Unknown",
    dot: "⚪",
  };
  return (
    <span className={current.className}>
      <span style={{ fontSize: 10 }}>{current.dot}</span>
      {current.label}
    </span>
  );
};

// ─── InterviewRoundsBuilder ────────────────────────────────────────────────────
const InterviewRoundsBuilder = ({ rounds, onChange, errors }) => {
  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [openEmpDropdown, setOpenEmpDropdown] = useState(null);
  const [employeeSearch, setEmployeeSearch] = useState("");

  useEffect(() => {
    setEmpLoading(true);
    axios
      .get(`${process.env.REACT_APP_COMMON_API_URL}/employees/list`)
      .then((res) => setEmployees(res.data.data || []))
      .catch(console.error)
      .finally(() => setEmpLoading(false));
  }, []);

  useEffect(() => {
    if (openEmpDropdown === null) return;
    const handler = (e) => {
      if (!e.target.closest(".jp-emp-dropdown-wrap")) setOpenEmpDropdown(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openEmpDropdown]);

  const updateRoundField = (idx, field, value) => {
    const updated = rounds.map((r, i) =>
      i === idx ? { ...r, [field]: value } : r,
    );
    onChange(updated);
  };

  const toggleEmployee = (idx, empId) => {
    const currentIds = rounds[idx].employeeIds || [];
    const nextIds = currentIds.includes(empId)
      ? currentIds.filter((id) => id !== empId)
      : [...currentIds, empId];
    updateRoundField(idx, "employeeIds", nextIds);
  };

  const toggleFlag = (idx, flag) => {
    const isTurningOn = !rounds[idx][flag];
    const updated = rounds.map((r, i) => {
      if (i === idx) return { ...r, [flag]: isTurningOn };
      if (isTurningOn) return { ...r, [flag]: false };
      return r;
    });
    onChange(updated);
  };

  const removeRound = (idx) => {
    const updated = rounds
      .filter((_, i) => i !== idx)
      .map((r, i) => ({ ...r, sequence: i + 1 }));
    onChange(updated);
  };

  return (
    <div className="jp-rounds">
      <div className="jp-rounds__header">
        <span className="jp-rounds__title">Interview Rounds & Panellists</span>
      </div>

      {/* ── Initial / Final guidance tip — shown once at the top ── */}
      <div
        style={{
          marginBottom: 14,
          padding: "8px 12px",
          background: "#f0f9ff",
          border: "1px solid #bae6fd",
          borderRadius: 8,
          fontSize: 11.5,
          color: "#0369a1",
          lineHeight: 1.6,
        }}
      >
        <span style={{ fontWeight: 700 }}>🚦 Initial</span>
        {" — "}First screening round (e.g. HR call). Candidates enter the
        pipeline here.
        {"  ·  "}
        <span style={{ fontWeight: 700 }}>🏁 Final</span>
        {" — "}Last decision round (e.g. Director interview, offer discussion).
        Clearing this moves a candidate to offer stage.
      </div>

      {rounds.map((r, idx) => {
        const selectedEmps = employees.filter((e) =>
          (r.employeeIds || []).includes(e.empId),
        );
        return (
          <div
            key={idx}
            className="jp-round-card"
            style={{
              border: "1px solid #e5e7eb",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "12px",
              background: "#fafafa",
              overflow: "visible",
              position: "relative",
              zIndex: openEmpDropdown === idx ? 10 : 1,
            }}
          >
            <div className="jp-grid-2">
              <F
                label={`Round ${r.sequence} Name`}
                required
                error={errors?.[`round_${idx}`]}
              >
                <input
                  className={`jp-input${errors?.[`round_${idx}`] ? " jp-input--error" : ""}`}
                  value={r.roundName}
                  placeholder="e.g. Initial Screening, Manager Discussion"
                  onChange={(e) =>
                    updateRoundField(idx, "roundName", e.target.value)
                  }
                />
              </F>
              <F label="Interview Mode">
                <select
                  className="jp-input"
                  value={r.interviewMode || "ONLINE"}
                  onChange={(e) =>
                    updateRoundField(idx, "interviewMode", e.target.value)
                  }
                >
                  {INTERVIEW_MODES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </F>
            </div>

            <F
              label="Interviewers"
              hint={
                empLoading
                  ? "Loading employees…"
                  : selectedEmps.length > 0
                    ? `${selectedEmps.length} selected`
                    : "Optional — select panellists"
              }
            >
              <div
                className="jp-emp-dropdown-wrap"
                style={{ position: "relative" }}
              >
                <button
                  type="button"
                  className="jp-input"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: empLoading ? "not-allowed" : "pointer",
                    textAlign: "left",
                    minHeight: "36px",
                    flexWrap: "wrap",
                    gap: "4px",
                    height: "auto",
                    padding: "6px 10px",
                  }}
                  onClick={() =>
                    !empLoading &&
                    setOpenEmpDropdown(openEmpDropdown === idx ? null : idx)
                  }
                  disabled={empLoading}
                >
                  <span
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 4,
                      flex: 1,
                    }}
                  >
                    {selectedEmps.length === 0 ? (
                      <span style={{ color: "#9ca3af", fontSize: 13 }}>
                        {empLoading
                          ? "Loading…"
                          : "Search & select interviewers"}{" "}
                      </span>
                    ) : (
                      selectedEmps.map((emp) => (
                        <span
                          key={emp.empId}
                          style={{
                            background: "#eff6ff",
                            color: "#1d4ed8",
                            borderRadius: "4px",
                            padding: "1px 6px",
                            fontSize: "11px",
                            fontWeight: 500,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          {emp.firstName} {emp.lastName}
                          <span
                            role="button"
                            style={{ cursor: "pointer", lineHeight: 1 }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              toggleEmployee(idx, emp.empId);
                            }}
                          >
                            ×
                          </span>
                        </span>
                      ))
                    )}
                  </span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    style={{
                      flexShrink: 0,
                      transform:
                        openEmpDropdown === idx ? "rotate(180deg)" : "none",
                      transition: "transform 0.15s",
                    }}
                  >
                    <path
                      d="M2 4l4 4 4-4"
                      stroke="#6b7280"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {openEmpDropdown === idx && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      left: 0,
                      right: 0,
                      zIndex: 9999,
                      background: "#fff",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                      maxHeight: "260px",
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #f3f4f6",
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 2,
                      }}
                    >
                      <input
                        autoFocus
                        className="jp-input"
                        placeholder="Search interviewer by name or email..."
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                      />
                    </div>

                    {employees
                      .filter((emp) => {
                        const q = employeeSearch.toLowerCase().trim();

                        if (!q) return true;

                        return (
                          `${emp.firstName} ${emp.lastName}`
                            .toLowerCase()
                            .includes(q) ||
                          (emp.companyEmail || "").toLowerCase().includes(q)
                        );
                      })
                      .map((emp) => {
                        const isChecked = (r.employeeIds || []).includes(
                          emp.empId,
                        );

                        return (
                          <label
                            key={emp.empId}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              padding: "8px 12px",
                              cursor: "pointer",
                              fontSize: "13px",
                              background: isChecked ? "#eff6ff" : "transparent",
                              borderBottom: "1px solid #f3f4f6",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleEmployee(idx, emp.empId)}
                              style={{ accentColor: "#2563eb" }}
                            />

                            <span style={{ flex: 1 }}>
                              <span
                                style={{ fontWeight: isChecked ? 600 : 400 }}
                              >
                                {emp.firstName} {emp.lastName}
                              </span>

                              <span
                                style={{
                                  marginLeft: 6,
                                  color: "#9ca3af",
                                  fontSize: 11,
                                }}
                              >
                                {emp.companyEmail}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                  </div>
                )}
              </div>
            </F>

            <F label="Description" style={{ marginTop: 8 }}>
              <input
                className="jp-input"
                value={r.description || ""}
                placeholder="e.g. Technical assessment with the engineering team"
                onChange={(e) =>
                  updateRoundField(idx, "description", e.target.value)
                }
              />
            </F>

            <div
              className="jp-round-actions"
              style={{
                marginTop: "10px",
                display: "flex",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <button
                type="button"
                className={`jp-round__flag${r.initial ? " jp-round__flag--initial" : ""}`}
                onClick={() => toggleFlag(idx, "initial")}
              >
                🚦 Initial
              </button>
              <button
                type="button"
                className={`jp-round__flag${r.final ? " jp-round__flag--final" : ""}`}
                onClick={() => toggleFlag(idx, "final")}
              >
                🏁 Final
              </button>
              {rounds.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeRound(idx)}
                  style={{
                    color: "#ef4444",
                    marginLeft: "auto",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M1.5 3h9M4 3V2h4v1M4.5 5.5v3M7.5 5.5v3M2.5 3l.5 7h6l.5-7"
                      stroke="#ef4444"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Remove
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* ── Bottom centered Add Round button ── */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
        <button
          type="button"
          className="jp-rounds__add"
          style={{
            borderRadius: "20px",
            padding: "7px 22px",
            fontSize: 13,
            fontWeight: 600,
          }}
          onClick={() =>
            onChange([
              ...rounds,
              {
                roundName: "",
                sequence: rounds.length + 1,
                interviewMode: "ONLINE",
                employeeIds: [],
                initial: false,
                final: false,
                description: "",
              },
            ])
          }
        >
          + Add Round
        </button>
      </div>
    </div>
  );
};

// ─── JobPostDetailsDrawer ─────────────────────────────────────────────────────
const JobPostDetailsDrawer = ({ post, onClose, onEdit }) => {
  const skills = post.skills
    ? post.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  // Normalise qualifications from response DTO
  const qualificationsList = Array.isArray(post.qualifications)
    ? post.qualifications
    : [];

  const Row = ({ label, value }) =>
    value ? (
      <div className="vd-row">
        <span className="vd-row__label">{label}</span>
        <span className="vd-row__val">{value}</span>
      </div>
    ) : null;

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">{post.jobTitle}</h2>
            <p className="drawer__sub">#{post.jobPostCode}</p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="drawer__body">
          <div style={{ marginBottom: 16 }}>
            <StatusBadge status={post.status} />
          </div>

          <div className="vd-section">
            <div className="vd-section__title">Job Details</div>
            <Row label="Department" value={post.department} />
            <Row label="Role" value={post.roleName} />
            <Row
              label="Employment Type"
              value={post.employmentType?.replace(/_/g, " ")}
            />
            <Row label="Positions" value={post.noOfVacancy} />
            <Row label="Experience" value={post.experience} />
            <Row
              label="Location"
              value={[post.branchCity, post.branchState, post.branchCountry]
                .filter(Boolean)
                .join(", ")}
            />
            <Row
              label="Closing Date"
              value={
                post.expectedClosingTime
                  ? new Date(post.expectedClosingTime).toLocaleDateString(
                      "en-IN",
                      { day: "2-digit", month: "short", year: "numeric" },
                    )
                  : null
              }
            />
            {/* Qualifications — show as chips */}
            {qualificationsList.length > 0 && (
              <div className="vd-row">
                <span className="vd-row__label">Qualifications</span>
                <span
                  className="vd-row__val"
                  style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                >
                  {qualificationsList.map((q) => (
                    <span key={q.qualificationId || q} className="jp-skill">
                      {q.qualificationName || q}
                    </span>
                  ))}
                </span>
              </div>
            )}
          </div>

          {post.budget > 0 && (
            <div className="vd-section">
              <div className="vd-section__title">Compensation</div>
              <Row
                label="Budget"
                value={`₹${Number(post.budget).toLocaleString("en-IN")} ${(post.budgetType || "").toUpperCase() === "MONTHLY" ? "/ month" : "/ year"} · ${(post.salaryType || "").toUpperCase()}`}
              />
              {post.budgetType === "MONTHLY" && (
                <Row
                  label="Annual Equivalent"
                  value={`₹${(Number(post.budget) * 12).toLocaleString("en-IN")}`}
                />
              )}
            </div>
          )}

          {skills.length > 0 && (
            <div className="vd-section">
              <div className="vd-section__title">Skills</div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                  marginTop: 6,
                }}
              >
                {skills.map((s) => (
                  <span key={s} className="jp-skill">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(post.jobDescription ||
            post.responsibilities ||
            post.desiredProfile) && (
            <div className="vd-section">
              <div className="vd-section__title">Job Content</div>
              {post.jobDescription && (
                <div style={{ marginBottom: 12 }}>
                  <div className="vd-row__label" style={{ marginBottom: 4 }}>
                    Job Description
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: "#374151",
                      lineHeight: 1.6,
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {post.jobDescription}
                  </p>
                </div>
              )}
              {post.responsibilities && (
                <div style={{ marginBottom: 12 }}>
                  <div className="vd-row__label" style={{ marginBottom: 4 }}>
                    Responsibilities
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: "#374151",
                      lineHeight: 1.6,
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {post.responsibilities}
                  </p>
                </div>
              )}
              {post.desiredProfile && (
                <div>
                  <div className="vd-row__label" style={{ marginBottom: 4 }}>
                    Desired Profile
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: "#374151",
                      lineHeight: 1.6,
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {post.desiredProfile}
                  </p>
                </div>
              )}
            </div>
          )}

          {(post.hiringFlowName || post.hiringFlow) && (
            <div className="vd-section">
              <div className="vd-section__title">Hiring Flow</div>
              <Row
                label="Flow Name"
                value={post.hiringFlowName || post.hiringFlow?.hiringFlowName}
              />
              {post.hiringFlow?.rounds?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {post.hiringFlow.rounds
                    .slice()
                    .sort((a, b) => a.sequence - b.sequence)
                    .map((r, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "5px 0",
                          fontSize: 13,
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <span
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: r.initial
                              ? "#dbeafe"
                              : r.final
                                ? "#dcfce7"
                                : "#f3f4f6",
                            color: r.initial
                              ? "#1d4ed8"
                              : r.final
                                ? "#16a34a"
                                : "#374151",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          {r.sequence}
                        </span>
                        <span style={{ flex: 1, color: "#111827" }}>
                          {r.roundName}
                        </span>
                        <span style={{ fontSize: 11, color: "#6b7280" }}>
                          {r.interviewMode}
                        </span>
                        {r.initial && (
                          <span
                            style={{
                              fontSize: 10,
                              background: "#dbeafe",
                              color: "#1d4ed8",
                              borderRadius: 4,
                              padding: "1px 5px",
                            }}
                          >
                            Initial
                          </span>
                        )}
                        {r.final && (
                          <span
                            style={{
                              fontSize: 10,
                              background: "#dcfce7",
                              color: "#16a34a",
                              borderRadius: 4,
                              padding: "1px 5px",
                            }}
                          >
                            Final
                          </span>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          <div className="vd-section">
            <div className="vd-section__title">Sharing</div>
            <Row
              label="Candidates Portal"
              value={post.shareToCandidates ? "Yes" : "No"}
            />
            <Row label="LinkedIn" value={post.postOnLinkedIn ? "Yes" : "No"} />
            <Row
              label="Naukri"
              value={post.postOnNakuri || post.postToNaukri ? "Yes" : "No"}
            />
            {post.consultancySharedResponseDTOList?.length > 0 && (
              <div
                className="vd-row"
                style={{ flexDirection: "column", alignItems: "flex-start" }}
              >
                <span className="vd-row__label" style={{ marginBottom: 6 }}>
                  Shared to Consultancies
                </span>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    width: "100%",
                  }}
                >
                  {post.consultancySharedResponseDTOList.map((c) => (
                    <div
                      key={c.connectionId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 10px",
                        background: "#f9fafb",
                        border: "1px solid #f3f4f6",
                        borderRadius: 6,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#111827",
                        }}
                      >
                        {c.connectionName || `Connection #${c.connectionId}`}
                      </span>
                      {c.remark && (
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          — {c.remark}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="drawer__footer">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            Close
          </button>
          <div style={{ flex: 1 }} />
          <button className="cv-btn cv-btn--primary" onClick={onEdit}>
            Edit Post
          </button>
        </div>
      </div>
    </>
  );
};

// ─── StatusChangeButtons ──────────────────────────────────────────────────────
const StatusChangeButtons = ({
  currentStatus,
  jobPostId,
  onStatusChanged,
  isManual = false,
}) => {
  const [updating, setUpdating] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  const changeStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const url = isManual
        ? `${CONSULTANT_API}/manual/jobpost/${jobPostId}/status`
        : `${COMPANY_API}/${jobPostId}/status`;
      await axios.put(url, { status: newStatus });
      const label = newStatus.charAt(0) + newStatus.slice(1).toLowerCase();
      toast.success(`Job post marked as ${label}.`);
      onStatusChanged();
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to update status.`);
    } finally {
      setUpdating(false);
      setPendingStatus(null);
    }
  };

  const actions = [];
  if (currentStatus === "OPEN") {
    actions.push({
      status: "CLOSED",
      label: "Close",
      cls: "jp-act-btn--status-close",
      icon: (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <circle
            cx="6.5"
            cy="6.5"
            r="5"
            stroke="currentColor"
            strokeWidth="1.3"
          />
          <path
            d="M4.5 6.5h4"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      ),
    });
    actions.push({
      status: "CANCELLED",
      label: "Cancel",
      cls: "jp-act-btn--status-cancel",
      icon: (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <circle
            cx="6.5"
            cy="6.5"
            r="5"
            stroke="currentColor"
            strokeWidth="1.3"
          />
          <path
            d="M4.5 4.5l4 4M8.5 4.5l-4 4"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
      ),
    });
  } else if (currentStatus === "CLOSED") {
    actions.push({
      status: "OPEN",
      label: "Reopen",
      cls: "jp-act-btn--status-open",
      icon: (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <circle
            cx="6.5"
            cy="6.5"
            r="5"
            stroke="currentColor"
            strokeWidth="1.3"
          />
          <path
            d="M6.5 4v2.5l1.5 1.5"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    });
    actions.push({
      status: "CANCELLED",
      label: "Cancel",
      cls: "jp-act-btn--status-cancel",
      icon: (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <circle
            cx="6.5"
            cy="6.5"
            r="5"
            stroke="currentColor"
            strokeWidth="1.3"
          />
          <path
            d="M4.5 4.5l4 4M8.5 4.5l-4 4"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
      ),
    });
  } else if (currentStatus === "CANCELLED") {
    actions.push({
      status: "OPEN",
      label: "Reopen",
      cls: "jp-act-btn--status-open",
      icon: (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <circle
            cx="6.5"
            cy="6.5"
            r="5"
            stroke="currentColor"
            strokeWidth="1.3"
          />
          <path
            d="M6.5 4v2.5l1.5 1.5"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    });
  }

  if (!actions.length) return null;

  return (
    <>
      {actions.map(({ status, label, cls, icon }) => (
        <button
          key={status}
          className={`jp-act-btn ${cls}`}
          title={`Mark as ${label}`}
          onClick={() => setPendingStatus(status)}
          disabled={updating}
        >
          {updating ? <span className="jp-act-spinner" /> : icon}
          {label}
        </button>
      ))}
      {createPortal(
        <WarningPopup
          isOpen={!!pendingStatus}
          onClose={() => setPendingStatus(null)}
          onDecline={() => setPendingStatus(null)}
          onAccept={() => changeStatus(pendingStatus)}
          title="Change Job Post Status"
          message={`Change status to ${pendingStatus ? pendingStatus.charAt(0) + pendingStatus.slice(1).toLowerCase() : ""}?`}
          acceptText={updating ? "Updating…" : "Confirm"}
          declineText="Cancel"
        />,
        document.body,
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  AllJobPosts — workspace-aware entry point
// ─────────────────────────────────────────────────────────────────────────────
const AllJobPosts = () => {
  const { isConsultant } = useWorkspace();
  return isConsultant ? <ConsultantJobPosts /> : <CompanyJobPosts />;
};

const STATUS_FILTER_OPTIONS = [
  { value: "ALL", label: "All", dot: null },
  { value: "OPEN", label: "Open", dot: "#22c55e" },
  { value: "CLOSED", label: "Closed", dot: "#6b7280" },
  { value: "CANCELLED", label: "Cancelled", dot: "#ef4444" },
];

const StatusFilterBar = ({ active, onChange, counts }) => (
  <div
    className="jp-status-filter"
    style={{
      display: "flex",
      gap: 6,
      flexWrap: "wrap",
      marginBottom: "10px",
    }}
  >
    {STATUS_FILTER_OPTIONS.map((opt) => {
      const count =
        opt.value === "ALL" ? counts.total : (counts[opt.value] ?? 0);
      const isActive = active === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 12px",
            borderRadius: 20,
            border: isActive ? "1.5px solid #2563eb" : "1.5px solid #e5e7eb",
            background: isActive ? "#eff6ff" : "#fff",
            color: isActive ? "#1d4ed8" : "#374151",
            fontSize: 12,
            fontWeight: isActive ? 600 : 400,
            cursor: "pointer",
            transition: "all 0.15s",
            whiteSpace: "nowrap",
          }}
        >
          {opt.dot && (
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: opt.dot,
                flexShrink: 0,
              }}
            />
          )}
          {opt.label}
          <span
            style={{
              background: isActive ? "#dbeafe" : "#f3f4f6",
              color: isActive ? "#1d4ed8" : "#6b7280",
              borderRadius: 10,
              padding: "1px 6px",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {count}
          </span>
        </button>
      );
    })}
  </div>
);

const exportJobPostsCSV = (data) => {
  const headers = [
    "Job Code",
    "Job Title",
    "Department",
    "Role",
    "Employment Type",
    "Experience",
    "Vacancies",
    "Budget",
    "Budget Type",
    "Salary Type",
    "Qualifications",
    "Location",
    "Status",
    "Closing Date",
    "Skills",
  ];
  const escape = (val) => {
    const str = val === null || val === undefined ? "" : String(val);
    return str.includes(",") || str.includes('"') || str.includes("\n")
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };
  const rows = data.map((p) => {
    const quals = Array.isArray(p.qualifications)
      ? p.qualifications.map((q) => q.qualificationName || q).join("; ")
      : "";
    return [
      p.jobPostCode || "",
      p.jobTitle || "",
      p.department || "",
      p.roleName || "",
      p.employmentType || "",
      p.experience || "",
      p.noOfVacancy || "",
      p.budget || "",
      p.budgetType || "",
      p.salaryType || "",
      quals,
      [p.branchCity, p.branchState, p.branchCountry].filter(Boolean).join(", "),
      p.status || "",
      p.expectedClosingTime ? p.expectedClosingTime.split("T")[0] : "",
      p.skills || "",
    ]
      .map(escape)
      .join(",");
  });
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `job-posts-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── TablePagination ──────────────────────────────────────────────────────────
const TablePagination = ({ page, total, pageSize, onChange }) => {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 2) pages.push(i);
    else if (pages[pages.length - 1] !== "…") pages.push("…");
  }
  return (
    <div className="jp-pagination">
      <span className="jp-pagination__info">
        {from}–{to} of {total}
      </span>
      <div className="jp-pagination__btns">
        <button
          className="jp-pagination__btn"
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
        >
          ‹ Prev
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className="jp-pagination__ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`jp-pagination__btn${p === page ? " jp-pagination__btn--active" : ""}`}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          className="jp-pagination__btn"
          disabled={page === totalPages}
          onClick={() => onChange(page + 1)}
        >
          Next ›
        </button>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
//  COMPANY JOB POSTS
// ═════════════════════════════════════════════════════════════════════════════
const CompanyJobPosts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [prefillVacancy, setPrefillVacancy] = useState(
    location.state?.vacancy || null,
  );
  const [forceDeleteTarget, setForceDeleteTarget] = useState(null);
  const [forceDeleting, setForceDeleting] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);
  const [detailPost, setDetailPost] = useState(null);
  const [tablePage, setTablePage] = useState(1);
  const TABLE_PAGE_SIZE = 50;
  const [highlightId, setHighlightId] = useState(
    location.state?.highlightJobPostId || null,
  );
  const [listViewMode, setListViewMode] = useState(
    () => localStorage.getItem("jobposts-view-mode") || "card",
  );
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    if (location.state?.highlightJobPostId) {
      setHighlightId(location.state.highlightJobPostId);
    }
  }, [location.state]);

  const filterCounts = useMemo(
    () => ({
      total: posts.length,
      OPEN: countByStatus(posts, "OPEN"),
      DRAFT: countByStatus(posts, "DRAFT"),
      CLOSED: countByStatus(posts, "CLOSED"),
      CANCELLED: countByStatus(posts, "CANCELLED"),
    }),
    [posts],
  );

  const filteredPosts = useMemo(
    () =>
      statusFilter === "ALL"
        ? posts
        : posts.filter((p) => getNormalizedStatus(p.status) === statusFilter),
    [posts, statusFilter],
  );

  useEffect(() => {
    setTablePage(1);
  }, [statusFilter]);
  useEffect(() => {
    localStorage.setItem("jobposts-view-mode", listViewMode);
  }, [listViewMode]);

  const { isRecruiterHr } = useWorkspace();

  const postStats = useMemo(
    () => [
      {
        label: "Total Posts",
        value: posts.length,
        tone: "blue",
        sub: `${countByStatus(posts, "OPEN")} open right now`,
      },
      {
        label: "Open Roles",
        value: countByStatus(posts, "OPEN"),
        tone: "green",
        sub: "Accepting candidates",
      },
      {
        label: "Closing Soon",
        value: posts.filter((post) => isClosingSoon(post.expectedClosingTime))
          .length,
        tone: "amber",
        sub: "Within the next 14 days",
      },
      {
        label: "Shared Externally",
        value: posts.filter(
          (post) =>
            post.postOnLinkedInUrl ||
            post.postOnNakuriUrl ||
            post.jobPostLink ||
            post.consultancySharedResponseDTOList?.length,
        ).length,
        tone: "violet",
        sub: "Published or shared out",
      },
    ],
    [posts],
  );

  const fetchPosts = useCallback(() => {
    setLoading(true);
    axios
      .get(`${COMPANY_API}/all`)
      .then((res) => setPosts(res.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    let pendingVacancy = location.state?.vacancy || null;

    if (!pendingVacancy) {
      try {
        const stored = sessionStorage.getItem("pendingJobPostVacancy");
        if (stored) pendingVacancy = JSON.parse(stored);
      } catch (e) {
        pendingVacancy = null;
      }
    }

    if (pendingVacancy) {
      setPrefillVacancy(pendingVacancy);
      setDrawerOpen(true);
      sessionStorage.removeItem("pendingJobPostVacancy");
    }
    // Runs once on mount — deliberately not tied to location.state so it
    // still fires even if something upstream clears state before this
    // component mounts (sessionStorage is read above as the fallback).
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (highlightId) {
      const t = setTimeout(() => setHighlightId(null), 3000);
      return () => clearTimeout(t);
    }
  }, [highlightId]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${COMPANY_API}/${id}`);
      toast.success("Job post deleted.");
      fetchPosts();
    } catch (err) {
      const message = err?.response?.data?.message || "";
      if (err?.response?.status === 409)
        setForceDeleteTarget({ id, conflictMessage: message });
      else toast.error(message || "Failed to delete job post.");
    }
  };

  const handleForceDelete = async () => {
    if (!forceDeleteTarget) return;
    setForceDeleting(true);
    try {
      await axios.delete(`${COMPANY_API}/${forceDeleteTarget.id}/force`);
      toast.success("Job post force deleted.");
      setForceDeleteTarget(null);
      fetchPosts();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Force delete failed.");
    } finally {
      setForceDeleting(false);
    }
  };

  return (
    <div className="jp-page">
      <div className="jp-page__header">
        <div className="jp-page__header-main">
          <div className="jp-page__title-wrap">
            <h2 className="jp-page__title">Job Posts</h2>
            <span className="jp-page__count">{posts.length} jobs</span>
          </div>
          <p className="jp-page__sub">
            Every open, draft, and shared role in one Hiring Flow-style view.
          </p>
        </div>
        <div
          className="jp-page__header-actions"
          style={{ display: "flex", gap: 10, alignItems: "center" }}
        >
          <button
            className="jp-top-action-btn"
            onClick={() => exportJobPostsCSV(filteredPosts)}
            disabled={filteredPosts.length === 0}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v8M5 7l3 3 3-3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 13h12"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            Export Excel
          </button>
          <LockedButton
            codes={[RECRUITMENT.JOB_POSTS_CREATE]}
            className="jp-create-btn"
            onClick={() => {
              setPrefillVacancy(null);
              setDrawerOpen(true);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v12M2 8h12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Post a Job
          </LockedButton>
        </div>
      </div>

      {!loading && posts.length > 0 && (
        <div className="jp-stats-row">
          {postStats.map((stat) => (
            <article
              key={stat.label}
              className={`jp-stat jp-stat--${stat.tone}`}
            >
              <span className="jp-stat__bar" />
              <div className="jp-stat__body">
                <div className="jp-stat__text">
                  <p className="jp-stat__label">{stat.label}</p>
                  <p className="jp-stat__sub">{stat.sub}</p>
                </div>
                <h3 className="jp-stat__value">{stat.value}</h3>
              </div>
            </article>
          ))}
        </div>
      )}

      <div className="jp-toolbar">
        {!loading && posts.length > 0 && (
          <StatusFilterBar
            active={statusFilter}
            onChange={setStatusFilter}
            counts={filterCounts}
          />
        )}
        <div className="jp-view-toggle">
          <button
            type="button"
            className={`jp-view-toggle__btn${listViewMode === "card" ? " jp-view-toggle__btn--active" : ""}`}
            onClick={() => setListViewMode("card")}
          >
            ⊞ Cards
          </button>
          <button
            type="button"
            className={`jp-view-toggle__btn${listViewMode === "table" ? " jp-view-toggle__btn--active" : ""}`}
            onClick={() => setListViewMode("table")}
          >
            ☰ Table
          </button>
        </div>
      </div>

      {loading ? (
        <div className="jp-loader">Loading job posts...</div>
      ) : posts.length === 0 ? (
        <div className="jp-empty">
          <span className="jp-empty__icon">📄</span>
          <p>No job posts yet.</p>
          <LockedButton
            codes={[RECRUITMENT.JOB_POSTS_CREATE]}
            className="jp-create-btn"
            onClick={() => setDrawerOpen(true)}
          >
            Create your first
          </LockedButton>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="jp-empty">
          <span className="jp-empty__icon">📭</span>
          <p>
            No {statusFilter !== "ALL" ? statusFilter.toLowerCase() : ""} job
            posts found.
          </p>
        </div>
      ) : listViewMode === "card" ? (
        <div className="jp-card-grid">
          {filteredPosts.map((p) => (
            <CompanyJobCard
              key={p.jobPostId}
              p={p}
              onDelete={handleDelete}
              onEdit={(post) => setEditTarget(post)}
              navigate={navigate}
              showStatusControls={isRecruiterHr}
              onStatusChanged={fetchPosts}
              setQrUrl={setQrUrl}
              highlight={highlightId === p.jobPostId}
              onDetail={(post) => setDetailPost(post)}
            />
          ))}
        </div>
      ) : (
        <div className="jp-table-wrapper">
          <table className="jp-table">
            <thead className="jp-table__head">
              <tr>
                <th>SL No</th>
                <th>Position</th>
                <th>Department</th>
                <th>Role</th>
                <th>Openings</th>
                <th>Experience</th>
                <th>Location</th>
                <th>Deadline</th>
                <th>Skills</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPosts
                .slice(
                  (tablePage - 1) * TABLE_PAGE_SIZE,
                  tablePage * TABLE_PAGE_SIZE,
                )
                .map((p, idx) => (
                  <CompanyJobTableRow
                    key={p.jobPostId}
                    p={p}
                    slNo={(tablePage - 1) * TABLE_PAGE_SIZE + idx + 1}
                    onDelete={handleDelete}
                    onEdit={(post) => setEditTarget(post)}
                    navigate={navigate}
                    showStatusControls={isRecruiterHr}
                    onStatusChanged={fetchPosts}
                    setQrUrl={setQrUrl}
                    highlight={highlightId === p.jobPostId}
                    onDetail={(post) => setDetailPost(post)}
                  />
                ))}
            </tbody>
          </table>
          {Math.ceil(filteredPosts.length / TABLE_PAGE_SIZE) > 1 && (
            <TablePagination
              page={tablePage}
              total={filteredPosts.length}
              pageSize={TABLE_PAGE_SIZE}
              onChange={setTablePage}
            />
          )}
        </div>
      )}

      {drawerOpen && (
        <CreateCompanyJobPostDrawer
          prefillVacancy={prefillVacancy}
          onClose={() => {
            setDrawerOpen(false);
            setPrefillVacancy(null);
          }}
          onCreated={() => {
            setDrawerOpen(false);
            setPrefillVacancy(null);
            fetchPosts();
            toast.success("Job post created!");
          }}
        />
      )}
      {editTarget && (
        <EditCompanyJobPostDrawer
          post={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={() => {
            setEditTarget(null);
            fetchPosts();
            toast.success("Job post updated!");
          }}
        />
      )}

      {qrUrl && <QRShareModal data={qrUrl} onClose={() => setQrUrl(null)} />}

      <WarningPopup
        isOpen={!!forceDeleteTarget}
        onClose={() => setForceDeleteTarget(null)}
        onDecline={() => setForceDeleteTarget(null)}
        onAccept={handleForceDelete}
        title="Force Delete Job Post"
        message={
          forceDeleteTarget?.conflictMessage
            ? `${forceDeleteTarget.conflictMessage} Do you want to force delete?`
            : "This job post has applied candidates. Do you want to force delete?"
        }
        acceptText={forceDeleting ? "Deleting..." : "Force Delete"}
        declineText="Cancel"
      />

      {detailPost && (
        <JobPostDetailsDrawer
          post={detailPost}
          onClose={() => setDetailPost(null)}
          onEdit={() => {
            setDetailPost(null);
            setEditTarget(detailPost);
          }}
        />
      )}
    </div>
  );
};

const CompanyJobCard = ({
  p,
  onDelete,
  onEdit,
  onDetail,
  navigate,
  showStatusControls,
  onStatusChanged,
  setQrUrl,
  highlight,
}) => {
  const skills = p.skills
    ? p.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const isClosed = ["CANCELLED", "CLOSED"].includes(
    (p.status || "").toUpperCase(),
  );
  const isExpired = isPastApplicationClosingDate(p);

  return (
    <div
      className={`jp-card jp-card--${(p.status || "").toLowerCase()}${highlight ? " jp-card--highlight" : ""}`}
    >
      <div className="jp-card__top">
        <div className="jp-card__title-wrap">
          <h3 className="jp-card__title">{p.jobTitle}</h3>
          <span className="jp-row__code">#{p.jobPostCode}</span>
        </div>
        <StatusBadge status={p.status} />
      </div>
      <div className="jp-card__meta">
        {p.department && (
          <div className="jp-card__meta-row">🏢 {p.department}</div>
        )}
        {p.roleName && <div className="jp-card__meta-row">🎯 {p.roleName}</div>}
        {p.noOfVacancy && (
          <div className="jp-card__meta-row">
            👥 {p.noOfVacancy} position{p.noOfVacancy !== 1 ? "s" : ""}
          </div>
        )}
        {p.experience && (
          <div className="jp-card__meta-row">🕐 {p.experience}</div>
        )}
        {(p.branchCity || p.branchState || p.branchCountry) && (
          <div className="jp-card__meta-row">
            📍{" "}
            {[p.branchCity, p.branchState, p.branchCountry]
              .filter(Boolean)
              .join(", ")}
          </div>
        )}
        {p.expectedClosingTime && (
          <div className="jp-card__meta-row jp-card__meta-row--closing">
            ⏳ Closes {p.expectedClosingTime.split("T")[0]}
          </div>
        )}
      </div>
      {skills.length > 0 && (
        <div className="jp-row__tags" style={{ marginTop: 8 }}>
          {skills.slice(0, 4).map((s) => (
            <span key={s} className="jp-skill">
              {s}
            </span>
          ))}
          {skills.length > 4 && (
            <span className="jp-skill jp-skill--more">
              +{skills.length - 4}
            </span>
          )}
        </div>
      )}
      <div className="jp-card__footer">
        {showStatusControls && (
          <StatusChangeButtons
            currentStatus={p.status}
            jobPostId={p.jobPostId}
            onStatusChanged={onStatusChanged}
          />
        )}
        <div className="jp-card__footer-icons">
          <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
            <IconTooltip label="Details">
              <button
                className="jp-icon-btn jp-icon-btn--detail"
                onClick={() => onDetail(p)}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect
                    x="1"
                    y="1"
                    width="12"
                    height="12"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.3"
                  />
                  <path
                    d="M4 4.5h6M4 7h6M4 9.5h4"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </IconTooltip>
            <IconTooltip label="View Candidates">
              <button
                className="jp-icon-btn jp-icon-btn--candidates"
                onClick={() =>
                  navigate("/recruitment/candidates/all", {
                    state: { jobPostId: p.jobPostId, jobTitle: p.jobTitle },
                  })
                }
              >
                <Icon icon="tabler:users-group" width={18} />
              </button>
            </IconTooltip>
            <IconTooltip label="View Interviews">
              <button
                className="jp-icon-btn jp-icon-btn--interviews"
                onClick={() =>
                  navigate("/recruitment/interviews", {
                    state: { jobPostId: p.jobPostId, jobTitle: p.jobTitle },
                  })
                }
              >
                <Icon icon="fluent:person-chat-24-regular" width={18} />
              </button>
            </IconTooltip>
          </div>
          {!isExpired && (
            <IconTooltip
              label={
                isClosed ? "Cannot edit a closed or cancelled job post" : "Edit"
              }
            >
              <button
                className={`jp-icon-btn jp-icon-btn--edit${isClosed ? " jp-icon-btn--disabled" : ""}`}
                onClick={isClosed ? undefined : () => onEdit(p)}
              >
                <svg width="14" height="14" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M9 2l2 2-6.5 6.5L2 11l.5-2.5L9 2z"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7.5 3.5l2 2"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </IconTooltip>
          )}
          {p.jobPostLink && !isExpired && (
            <ShareMenu
              link={p.jobPostLink}
              onShowQR={() => setQrUrl({ url: p.jobPostLink, post: p })}
              iconOnly
            />
          )}
          <IconTooltip label="Delete">
            <button
              className="jp-icon-btn jp-icon-btn--delete"
              onClick={() => onDelete(p.jobPostId)}
            >
              <svg width="14" height="14" viewBox="0 0 13 13" fill="none">
                <path
                  d="M2 3.5h9M5 3.5V2h3v1.5M5.5 6v4M7.5 6v4M3 3.5l.5 7.5h6L10 3.5"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </IconTooltip>
        </div>
      </div>
    </div>
  );
};

const CompanyJobTableRow = ({
  p,
  slNo,
  onDelete,
  onEdit,
  onDetail,
  navigate,
  showStatusControls,
  onStatusChanged,
  setQrUrl,
  highlight,
}) => {
  const isClosed = ["CANCELLED", "CLOSED"].includes(
    (p.status || "").toUpperCase(),
  );
  const isExpired = isPastApplicationClosingDate(p);
  const skills = p.skills
    ? p.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const location = [p.branchCity, p.branchState, p.branchCountry]
    .filter(Boolean)
    .join(", ");

  return (
    <tr
      className={`jp-table-row jp-table-row--${(p.status || "").toLowerCase()}${highlight ? " jp-table-row--highlight" : ""}`}
    >
      <td className="jp-tbl__td jp-tbl__td--slno">{slNo}</td>
      <td className="jp-tbl__td jp-tbl__td--title">
        <span className="jp-tbl__job-title">{p.jobTitle || "—"}</span>
        <span className="jp-tbl__job-code">#{p.jobPostCode}</span>
      </td>
      <td className="jp-tbl__td">
        {p.department ? (
          <span className="jp-tbl__meta">{p.department}</span>
        ) : (
          <span className="jp-tbl__na">—</span>
        )}
      </td>
      <td className="jp-tbl__td">
        {p.roleName ? (
          <span className="jp-tbl__meta">{p.roleName}</span>
        ) : (
          <span className="jp-tbl__na">—</span>
        )}
      </td>
      <td className="jp-tbl__td">
        {p.noOfVacancy ? (
          <span className="jp-tbl__meta">{p.noOfVacancy}</span>
        ) : (
          <span className="jp-tbl__na">—</span>
        )}
      </td>
      <td className="jp-tbl__td">
        {p.experience ? (
          <span className="jp-tbl__meta">{p.experience}</span>
        ) : (
          <span className="jp-tbl__na">—</span>
        )}
      </td>
      <td className="jp-tbl__td">
        {location ? (
          <span className="jp-tbl__meta">{location}</span>
        ) : (
          <span className="jp-tbl__na">—</span>
        )}
      </td>
      <td className="jp-tbl__td">
        {p.expectedClosingTime ? (
          <span className="jp-tbl__meta jp-tbl__meta--closing">
            {p.expectedClosingTime.split("T")[0]}
          </span>
        ) : (
          <span className="jp-tbl__na">—</span>
        )}
      </td>
      <td className="jp-tbl__td">
        <div className="jp-tbl__tags">
          {skills.slice(0, 3).map((s) => (
            <span key={s} className="jp-tbl__skill">
              {s}
            </span>
          ))}
          {skills.length > 3 && (
            <span className="jp-tbl__skill jp-tbl__skill--more">
              +{skills.length - 3}
            </span>
          )}
          {!skills.length && <span className="jp-tbl__na">—</span>}
        </div>
      </td>
      <td className="jp-tbl__td">
        <StatusBadge status={p.status} />
      </td>
      <td className="jp-tbl__td jp-tbl__td--actions">
        <div className="jp-tbl__actions">
          {showStatusControls && (
            <StatusChangeButtons
              currentStatus={p.status}
              jobPostId={p.jobPostId}
              onStatusChanged={onStatusChanged}
            />
          )}
          <button
            className="jp-tbl__btn jp-tbl__btn--detail"
            onClick={() => onDetail(p)}
            title="Details"
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
              <rect
                x="1"
                y="1"
                width="12"
                height="12"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path
                d="M4 4.5h6M4 7h6M4 9.5h4"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button
            className="jp-tbl__btn jp-tbl__btn--candidates"
            onClick={() =>
              navigate("/recruitment/candidates/all", {
                state: { jobPostId: p.jobPostId, jobTitle: p.jobTitle },
              })
            }
            title="View candidates"
          >
            <Icon icon="tabler:users-group" width={18} />
          </button>
          <button
            className="jp-tbl__btn jp-tbl__btn--interviews"
            onClick={() =>
              navigate("/recruitment/interviews", {
                state: { jobPostId: p.jobPostId, jobTitle: p.jobTitle },
              })
            }
            title="View interviews"
          >
            <Icon icon="fluent:person-chat-24-regular" width={18} />
          </button>
          {!isExpired && (
            <button
              className={`jp-tbl__btn jp-tbl__btn--edit${isClosed ? " jp-tbl__btn--disabled" : ""}`}
              onClick={isClosed ? undefined : () => onEdit(p)}
              disabled={isClosed}
              title={
                isClosed ? "Cannot edit a closed or cancelled job post" : "Edit"
              }
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path
                  d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          {p.jobPostLink && !isExpired && (
            <ShareMenu
              link={p.jobPostLink}
              onShowQR={() => setQrUrl({ url: p.jobPostLink, post: p })}
              iconOnly
            />
          )}
          <button
            className="jp-tbl__btn jp-tbl__btn--delete"
            onClick={() => onDelete(p.jobPostId)}
            title="Delete"
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 4h10M6 4V3h4v1M5 4v9h6V4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
//  CONSULTANT JOB POSTS
// ═════════════════════════════════════════════════════════════════════════════
const ConsultantJobPosts = () => {
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [publishTarget, setPublishTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [forceDeleteTarget, setForceDeleteTarget] = useState(null);
  const [forceDeleting, setForceDeleting] = useState(false);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("consultant-jobposts-mode") || "requirement",
  );
  const [prefillRequirementId, setPrefillRequirementId] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [listViewMode, setListViewMode] = useState(
    () => localStorage.getItem("consultant-jobposts-view-mode") || "card",
  );
  const [statusFilter, setStatusFilter] = useState("ALL");

  const filterCounts = useMemo(
    () => ({
      total: posts.length,
      OPEN: countByStatus(posts, "OPEN"),
      DRAFT: countByStatus(posts, "DRAFT"),
      CLOSED: countByStatus(posts, "CLOSED"),
      CANCELLED: countByStatus(posts, "CANCELLED"),
    }),
    [posts],
  );
  const filteredPosts = useMemo(
    () =>
      statusFilter === "ALL"
        ? posts
        : posts.filter((p) => getNormalizedStatus(p.status) === statusFilter),
    [posts, statusFilter],
  );

  useEffect(() => {
    localStorage.setItem("consultant-jobposts-mode", viewMode);
  }, [viewMode]);
  useEffect(() => {
    localStorage.setItem("consultant-jobposts-view-mode", listViewMode);
  }, [listViewMode]);

  const postStats = useMemo(
    () => [
      {
        label: "Visible Posts",
        value: posts.length,
        tone: "blue",
        sub:
          viewMode === "manual"
            ? "Manual job posts"
            : "Requirement-linked consultancy posts",
      },
      {
        label: "Open Roles",
        value: countByStatus(posts, "OPEN"),
        tone: "green",
        sub: "Currently accepting candidates",
      },
      {
        label: "Closing Soon",
        value: posts.filter((post) => isClosingSoon(post.expectedClosingTime))
          .length,
        tone: "amber",
        sub: "Within the next 14 days",
      },
      {
        label: "Published",
        value: posts.filter(
          (post) =>
            post.postOnLinkedIn || post.postOnNakuri || post.jobPostLink,
        ).length,
        tone: "violet",
        sub: "Externally visible roles",
      },
    ],
    [posts, viewMode],
  );

  const fetchPosts = useCallback(() => {
    setLoading(true);
    axios
      .get(`${CONSULTANT_API}/getAll`)
      .then((res) => {
        const all = res.data.data || [];
        setPosts(
          viewMode === "manual"
            ? all.filter((p) => !p.candidateRequirementId)
            : all.filter((p) => !!p.candidateRequirementId),
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [viewMode]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    if (location.state?.openCreateDrawer) {
      setViewMode("requirement");
      setPrefillRequirementId(location.state.candidateRequirementId ?? null);
      setDrawerOpen(true);
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${CONSULTANT_API}/delete/${id}`);
      toast.success("Job post deleted.");
      fetchPosts();
    } catch (err) {
      const message = err?.response?.data?.message || "";
      if (err?.response?.status === 409)
        setForceDeleteTarget({ id, conflictMessage: message });
      else toast.error(message || "Failed to delete.");
    }
  };

  const handleForceDelete = async () => {
    if (!forceDeleteTarget) return;
    setForceDeleting(true);
    try {
      await axios.delete(
        `${CONSULTANT_API}/delete/${forceDeleteTarget.id}/force`,
      );
      toast.success("Job post force deleted.");
      setForceDeleteTarget(null);
      fetchPosts();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Force delete failed.");
    } finally {
      setForceDeleting(false);
    }
  };

  return (
    <div className="jp-page">
      <div className="jp-page__header">
        <div className="jp-page__header-main">
          <div className="jp-page__title-wrap">
            <h2 className="jp-page__title">All Job Posts</h2>
            <span className="jp-page__count">{posts.length} jobs</span>
          </div>
          <p className="jp-page__sub">
            The same structured Hiring Flow look for consultancy postings too.
          </p>
        </div>
        <div className="jp-page__header-actions">
          <div className="jp-view-toggle">
            <button
              type="button"
              className={`jp-view-toggle__btn${viewMode === "requirement" ? " jp-view-toggle__btn--active" : ""}`}
              onClick={() => setViewMode("requirement")}
            >
              Company Requirement
            </button>
            <button
              type="button"
              className={`jp-view-toggle__btn${viewMode === "manual" ? " jp-view-toggle__btn--active" : ""}`}
              onClick={() => setViewMode("manual")}
            >
              Manual Creation
            </button>
          </div>
          <div className="jp-view-toggle">
            <button
              type="button"
              className={`jp-view-toggle__btn${listViewMode === "card" ? " jp-view-toggle__btn--active" : ""}`}
              onClick={() => setListViewMode("card")}
            >
              ⊞ Cards
            </button>
            <button
              type="button"
              className={`jp-view-toggle__btn${listViewMode === "table" ? " jp-view-toggle__btn--active" : ""}`}
              onClick={() => setListViewMode("table")}
            >
              ☰ Table
            </button>
          </div>
          <button
            className="jp-create-btn"
            onClick={() => exportJobPostsCSV(filteredPosts)}
            disabled={filteredPosts.length === 0}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v8M5 7l3 3 3-3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 13h12"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            Export Excel
          </button>
          <LockedButton
            codes={[CONSULTANCY.JOB_POSTS_CREATE]}
            className="jp-create-btn"
            onClick={() => setDrawerOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v12M2 8h12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Post a Job
          </LockedButton>
        </div>
      </div>

      {!loading && posts.length > 0 && (
        <div className="jp-stats-row">
          {postStats.map((stat) => (
            <article
              key={stat.label}
              className={`jp-stat jp-stat--${stat.tone}`}
            >
              <span className="jp-stat__bar" />
              <div className="jp-stat__body">
                <div className="jp-stat__text">
                  <p className="jp-stat__label">{stat.label}</p>
                  <p className="jp-stat__sub">{stat.sub}</p>
                </div>
                <h3 className="jp-stat__value">{stat.value}</h3>
              </div>
            </article>
          ))}
        </div>
      )}

      {!loading && posts.length > 0 && (
        <StatusFilterBar
          active={statusFilter}
          onChange={setStatusFilter}
          counts={filterCounts}
        />
      )}

      {loading ? (
        <div className="jp-loader">Loading job posts...</div>
      ) : posts.length === 0 ? (
        <div className="jp-empty">
          <span className="jp-empty__icon">📄</span>
          <p>
            No {viewMode === "manual" ? "manual" : "requirement-based"} job
            posts yet.
          </p>
          <LockedButton
            codes={[CONSULTANCY.JOB_POSTS_CREATE]}
            className="jp-create-btn"
            onClick={() => setDrawerOpen(true)}
          >
            Create your first
          </LockedButton>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="jp-empty">
          <span className="jp-empty__icon">📭</span>
          <p>
            No {statusFilter !== "ALL" ? statusFilter.toLowerCase() : ""} job
            posts found.
          </p>
        </div>
      ) : listViewMode === "card" ? (
        <div className="jp-card-grid">
          {filteredPosts.map((p) => (
            <ConsultantJobCard
              key={p.consultancyJobPostId}
              p={p}
              onDelete={handleDelete}
              onPublish={() => setPublishTarget(p)}
              onEdit={viewMode === "manual" ? () => setEditTarget(p) : null}
              isManual={viewMode === "manual"}
              onStatusChanged={fetchPosts}
              setQrUrl={setQrUrl}
            />
          ))}
        </div>
      ) : (
        <div className="jp-table-wrapper">
          <table className="jp-table">
            <thead>
              <tr>
                <th>Position</th>
                <th>Department</th>
                <th>Openings</th>
                <th>Experience</th>
                <th>Salary</th>
                <th>Deadline</th>
                <th>Skills</th>
                <th>Channels</th>
                <th>Status</th>
                <th>Controls</th>
              </tr>
            </thead>
            <tbody>
              {filteredPosts.map((p) => (
                <ConsultantJobTableRow
                  key={p.consultancyJobPostId}
                  p={p}
                  onDelete={handleDelete}
                  onPublish={() => setPublishTarget(p)}
                  onEdit={viewMode === "manual" ? () => setEditTarget(p) : null}
                  isManual={viewMode === "manual"}
                  onStatusChanged={fetchPosts}
                  setQrUrl={setQrUrl}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawerOpen && (
        <CreateConsultantJobPostDrawer
          prefillRequirementId={prefillRequirementId}
          onClose={() => {
            setDrawerOpen(false);
            setPrefillRequirementId(null);
          }}
          onCreated={() => {
            setDrawerOpen(false);
            setPrefillRequirementId(null);
            fetchPosts();
            toast.success("Job post created!");
          }}
        />
      )}
      {publishTarget && (
        <PublishConsultantJobPostDrawer
          post={publishTarget}
          onClose={() => setPublishTarget(null)}
          onDone={() => {
            setPublishTarget(null);
            fetchPosts();
            toast.success("Publishing options updated!");
          }}
        />
      )}
      {editTarget && (
        <EditConsultantManualDrawer
          post={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={() => {
            setEditTarget(null);
            fetchPosts();
            toast.success("Job post updated!");
          }}
        />
      )}

      <WarningPopup
        isOpen={!!forceDeleteTarget}
        onClose={() => setForceDeleteTarget(null)}
        onDecline={() => setForceDeleteTarget(null)}
        onAccept={handleForceDelete}
        title="Force Delete Job Post"
        message={
          forceDeleteTarget?.conflictMessage
            ? `${forceDeleteTarget.conflictMessage} Do you want to force delete?`
            : "This job post is OPEN and cannot be deleted normally. Do you want to force delete?"
        }
        acceptText={forceDeleting ? "Deleting..." : "Force Delete"}
        declineText="Cancel"
      />

      {qrUrl && <QRShareModal data={qrUrl} onClose={() => setQrUrl(null)} />}
    </div>
  );
};

const ConsultantJobTableRow = ({
  p,
  onDelete,
  onPublish,
  onEdit,
  isManual,
  onStatusChanged,
  setQrUrl,
  highlight,
}) => {
  const isClosed = ["CANCELLED", "CLOSED"].includes(
    (p.status || "").toUpperCase(),
  );
  const isExpired = isPastApplicationClosingDate(p);
  const navigate = useNavigate();

  const skills = p.skills
    ? p.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  return (
    <tr
      className={`jp-table-row jp-table-row--${(p.status || "").toLowerCase()}${highlight ? " jp-tr--highlight" : ""}`}
    >
      <td className="jp-tbl__td jp-tbl__td--title">
        <span className="jp-tbl__job-title">
          {p.consultancyJobTitle || p.roleName || "—"}
        </span>
        <span className="jp-tbl__job-code">#{p.jobPostCode}</span>
      </td>
      <td className="jp-tbl__td">
        {p.departmentName ? (
          <span className="jp-tbl__meta">{p.departmentName}</span>
        ) : (
          <span className="jp-tbl__na">—</span>
        )}
      </td>
      <td className="jp-tbl__td">
        {p.noOfVacancy ? (
          <span className="jp-tbl__meta">{p.noOfVacancy}</span>
        ) : (
          <span className="jp-tbl__na">—</span>
        )}
      </td>
      <td className="jp-tbl__td">
        {p.experience ? (
          <span className="jp-tbl__meta">{p.experience}</span>
        ) : (
          <span className="jp-tbl__na">—</span>
        )}
      </td>
      <td>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {p.budget > 0 ? (
            <>
              <span
                className={`jp-compensation jp-compensation--${(p.status || "").toLowerCase()}`}
              >
                ₹{Number(p.budget).toLocaleString()}
                <span className="jp-compensation__type">
                  {p.budgetType === "MONTHLY" ? "/month" : "/year"}
                </span>
              </span>
              {p.budgetType === "MONTHLY" && (
                <span className="jp-compensation__annual">
                  ₹{(Number(p.budget) * 12).toLocaleString()} annual
                </span>
              )}
            </>
          ) : (
            <span className="jp-table-na">—</span>
          )}
        </div>
      </td>
      <td className="jp-tbl__td">
        {p.expectedClosingTime ? (
          <span className="jp-tbl__meta jp-tbl__meta--closing">
            {p.expectedClosingTime.split("T")[0]}
          </span>
        ) : (
          <span className="jp-tbl__na">—</span>
        )}
      </td>
      <td className="jp-tbl__td">
        <div className="jp-tbl__tags">
          {skills.slice(0, 3).map((s) => (
            <span key={s} className="jp-tbl__skill">
              {s}
            </span>
          ))}
          {skills.length > 3 && (
            <span className="jp-tbl__skill jp-tbl__skill--more">
              +{skills.length - 3}
            </span>
          )}
          {!skills.length && <span className="jp-tbl__na">—</span>}
        </div>
      </td>
      <td className="jp-tbl__td">
        <div className="jp-tbl__tags">
          {p.postOnLinkedIn && (
            <span className="jp-tbl__channel jp-tbl__channel--linkedin">
              LinkedIn
            </span>
          )}
          {p.postOnNakuri && (
            <span className="jp-tbl__channel jp-tbl__channel--naukri">
              Naukri
            </span>
          )}
          {!p.postOnLinkedIn && !p.postOnNakuri && (
            <span className="jp-tbl__na">—</span>
          )}
        </div>
      </td>
      <td className="jp-tbl__td">
        <StatusBadge status={p.status} />
      </td>
      <td className="jp-tbl__td jp-tbl__td--actions">
        <div className="jp-tbl__actions">
          {isManual && (
            <StatusChangeButtons
              currentStatus={p.status}
              jobPostId={p.consultancyJobPostId}
              onStatusChanged={onStatusChanged}
              isManual={true}
            />
          )}
          <button
            className="jp-tbl__btn jp-tbl__btn--publish"
            onClick={onPublish}
            title="Publish options"
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path
                d="M6.5 1v7M3.5 5l3-4 3 4"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M1 10.5h11"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
          </button>
          {onEdit && !isExpired && (
            <button
              className={`jp-tbl__btn jp-tbl__btn--edit${isClosed ? " jp-tbl__btn--disabled" : ""}`}
              onClick={isClosed ? undefined : onEdit}
              disabled={isClosed}
              title={isClosed ? "Cannot edit" : "Edit"}
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path
                  d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          {p.jobPostLink && !isExpired && (
            <ShareMenu
              link={p.jobPostLink}
              onShowQR={() => setQrUrl({ url: p.jobPostLink, post: p })}
              iconOnly
            />
          )}

          <button
            className="jp-tbl__btn jp-tbl__btn--candidates"
            onClick={() =>
              navigate("/consultant/candidates/all", {
                state: {
                  jobPostId: p.consultancyJobPostId,
                  jobTitle: p.consultancyJobTitle || p.roleName,
                },
              })
            }
            title="Candidates"
          >
            <Icon icon="tabler:users-group" width={15} />
          </button>

          {/* NEW: Share Candidates */}
          {!isExpired && (
            <button
              className="jp-tbl__btn"
              onClick={() =>
                navigate("/consultant/share-candidates", {
                  state: {
                    jobPostId: p.consultancyJobPostId,
                    jobTitle: p.consultancyJobTitle || p.roleName,
                  },
                })
              }
              title="Share Candidates"
              style={{ color: "#0f6e56" }}
            >
              <Icon icon="mdi:share-variant-outline" width={15} />
            </button>
          )}

          {/* NEW: Job Drives */}
          <button
            className="jp-tbl__btn"
            onClick={() =>
              navigate("/consultant/job-drives/all", {
                state: {
                  jobPostId: p.consultancyJobPostId,
                  jobTitle: p.consultancyJobTitle || p.roleName,
                },
              })
            }
            title="Job Drives"
            style={{ color: "#d97706" }}
          >
            <Icon icon="mdi:calendar-star-outline" width={15} />
          </button>

          {/* NEW: company requirement (only if linked) */}
          {p.candidateRequirementId && (
            <button
              className="jp-tbl__btn"
              onClick={() =>
                navigate("/consultant/candidate-requirement", {
                  state: { requirementId: p.candidateRequirementId },
                })
              }
              title="Company Requirement"
              style={{ color: "#be185d" }}
            >
              <Icon icon="mdi:file-document-outline" width={15} />
            </button>
          )}

          <button
            className="jp-tbl__btn jp-tbl__btn--delete"
            onClick={() => onDelete(p.consultancyJobPostId)}
            title="Delete"
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 4h10M6 4V3h4v1M5 4v9h6V4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );
};

const ConsultantJobCard = ({
  p,
  onDelete,
  onPublish,
  onEdit,
  isManual,
  onStatusChanged,
  setQrUrl,
}) => {
  const skills = p.skills
    ? p.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const isClosed = ["CANCELLED", "CLOSED"].includes(
    (p.status || "").toUpperCase(),
  );
  const isExpired = isPastApplicationClosingDate(p);
  const navigate = useNavigate();
  const isOpen = (p.status || "").toUpperCase() === "OPEN";

  // Display qualifications from response DTO
  const qualificationsList = Array.isArray(p.qualifications)
    ? p.qualifications
    : [];

  return (
    <div className={`jp-card jp-card--${(p.status || "").toLowerCase()}`}>
      <div className="jp-card__top">
        <div className="jp-card__title-wrap">
          <h3 className="jp-card__title">
            {p.consultancyJobTitle || p.roleName || "—"}
          </h3>
          <span className="jp-row__code">#{p.jobPostCode}</span>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 4,
          }}
        >
          <StatusBadge status={p.status} />
          {isManual && (
            <span
              className="jp-chip"
              style={{ background: "#f5f3ff", color: "#7c3aed", fontSize: 10 }}
            >
              ✍️ Manual
            </span>
          )}
        </div>
      </div>
      <div className="jp-card__meta">
        {p.departmentName && (
          <div className="jp-card__meta-row">🏢 {p.departmentName}</div>
        )}
        {p.noOfVacancy && (
          <div className="jp-card__meta-row">
            👥 {p.noOfVacancy} position{p.noOfVacancy !== 1 ? "s" : ""}
          </div>
        )}
        {p.experience && (
          <div className="jp-card__meta-row">🕐 {p.experience}</div>
        )}
        {p.budget > 0 && (
          <div className="jp-card__meta-row">
            💰 ₹{Number(p.budget).toLocaleString()}
          </div>
        )}
        {qualificationsList.length > 0 && (
          <div className="jp-card__meta-row">
            🎓{" "}
            {qualificationsList.map((q) => q.qualificationName || q).join(", ")}
          </div>
        )}
        {p.expectedClosingTime && (
          <div className="jp-card__meta-row jp-card__meta-row--closing">
            ⏳ Closes {p.expectedClosingTime.split("T")[0]}
          </div>
        )}
        {p.employeeName && (
          <div className="jp-card__meta-row">👤 {p.employeeName}</div>
        )}
      </div>
      {skills.length > 0 && (
        <div className="jp-row__tags" style={{ marginTop: 6 }}>
          {skills.slice(0, 4).map((s) => (
            <span key={s} className="jp-skill">
              {s}
            </span>
          ))}
          {skills.length > 4 && (
            <span className="jp-skill jp-skill--more">
              +{skills.length - 4}
            </span>
          )}
        </div>
      )}
      {(p.postOnLinkedIn || p.postOnNakuri) && (
        <div className="jp-row__tags" style={{ marginTop: 4 }}>
          {p.postOnLinkedIn && (
            <span className="jp-channel jp-channel--linkedin">LinkedIn</span>
          )}
          {p.postOnNakuri && (
            <span className="jp-channel jp-channel--naukri">Naukri</span>
          )}
        </div>
      )}
      {p.description && (
        <p
          style={{
            fontSize: 11,
            color: "#6b7280",
            lineHeight: 1.5,
            marginTop: 4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {p.description}
        </p>
      )}
      <div className="jp-card__footer">
        {isManual && (
          <StatusChangeButtons
            currentStatus={p.status}
            jobPostId={p.consultancyJobPostId}
            onStatusChanged={onStatusChanged}
            isManual={true}
          />
        )}
        <div className="jp-publish-wrap">
          <div className="jp-publish-hover">
            <button
              className="jp-act-btn jp-act-btn--publish"
              onClick={isOpen ? onPublish : undefined}
              disabled={!isOpen}
            >
              <svg width="12" height="12" viewBox="0 0 13 13" fill="none">
                <path
                  d="M6.5 1v7M3.5 5l3-4 3 4"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M1 10.5h11"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
              Publish
            </button>
          </div>
          {!isOpen && (
            <div className="jp-publish-tooltip">
              Only open job posts can be published
            </div>
          )}
        </div>
        <div className="jp-card__footer-sep" />
        <div className="jp-card__footer-icons">
          <button
            className="jp-icon-btn jp-icon-btn--candidates"
            onClick={() =>
              navigate("/consultant/candidates/all", {
                state: {
                  jobPostId: p.consultancyJobPostId,
                  jobTitle: p.consultancyJobTitle || p.roleName,
                },
              })
            }
            title="Candidates"
          >
            <Icon icon="tabler:users-group" width={16} />
          </button>

          {/* NEW: Share Candidates */}
          {!isExpired && (
            <button
              className="jp-icon-btn"
              onClick={() =>
                navigate("/consultant/share-candidates", {
                  state: {
                    jobPostId: p.consultancyJobPostId,
                    jobTitle: p.consultancyJobTitle || p.roleName,
                  },
                })
              }
              title="Share Candidates"
              style={{ color: "#0f6e56" }}
            >
              <Icon icon="mdi:share-variant-outline" width={16} />
            </button>
          )}

          {/* NEW: Job Drives */}
          <button
            className="jp-icon-btn"
            onClick={() =>
              navigate("/consultant/job-drives/all", {
                state: {
                  jobPostId: p.consultancyJobPostId,
                  jobTitle: p.consultancyJobTitle || p.roleName,
                },
              })
            }
            title="Job Drives"
            style={{ color: "#d97706" }}
          >
            <Icon icon="mdi:calendar-star-outline" width={16} />
          </button>

          {/* NEW: company requirement */}
          {p.candidateRequirementId && (
            <button
              className="jp-icon-btn"
              onClick={() =>
                navigate("/consultant/candidate-requirement", {
                  state: { requirementId: p.candidateRequirementId },
                })
              }
              title="Company Requirement"
              style={{ color: "#be185d" }}
            >
              <Icon icon="mdi:file-document-outline" width={16} />
            </button>
          )}

          {onEdit && !isExpired && (
            <button
              className={`jp-icon-btn jp-icon-btn--edit${isClosed ? " jp-icon-btn--disabled" : ""}`}
              onClick={isClosed ? undefined : onEdit}
              title={isClosed ? "Cannot edit" : "Edit"}
            >
              <svg width="14" height="14" viewBox="0 0 13 13" fill="none">
                <path
                  d="M9 2l2 2-6.5 6.5L2 11l.5-2.5L9 2z"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.5 3.5l2 2"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
          {p.jobPostLink && !isExpired && (
            <ShareMenu
              link={p.jobPostLink}
              onShowQR={() => setQrUrl({ url: p.jobPostLink, post: p })}
              iconOnly
            />
          )}
          <button
            className="jp-icon-btn jp-icon-btn--delete"
            onClick={() => onDelete(p.consultancyJobPostId)}
            title="Delete"
          >
            <svg width="14" height="14" viewBox="0 0 13 13" fill="none">
              <path
                d="M2 3.5h9M5 3.5V2h3v1.5M5.5 6v4M7.5 6v4M3 3.5l.5 7.5h6L10 3.5"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
//  EditConsultantManualDrawer
// ═════════════════════════════════════════════════════════════════════════════
const EDIT_CONSULTANT_MANUAL_STEPS = ["Job Details", "Publishing Options"];

function postToConsultantEditForm(p) {
  return {
    consultancyJobTitle: p.consultancyJobTitle || "",
    jobDescription: p.description || p.jobDescription || "",
    roleDetails: p.roleDetails || "",
    roleId: p.roleId ? String(p.roleId) : "",
    departmentId: p.departmentId ? String(p.departmentId) : "",
    department: p.departmentName || p.department || "",
    branchId: p.branchId ? String(p.branchId) : "",
    budget: p.budget != null ? String(p.budget) : "",
    budgetDisplay: p.budget ? toIndianFormat(p.budget) : "",
    budgetType: p.budgetType ? p.budgetType.toUpperCase() : "ANNUAL",
    salaryType: p.salaryType ? p.salaryType.toUpperCase() : "CTC",
    experience: p.experience || "",
    noOfVacancy: p.noOfVacancy || 1,
    skills: p.skills || "",
    // qualificationIds: normalise from response
    qualificationIds: extractQualificationIds(p),
    expectedClosingTime: p.expectedClosingTime
      ? p.expectedClosingTime.slice(0, 16)
      : "",
    shareToCandidates: p.shareToCandidates ?? p.sharedToCandidates ?? false,
    postOnLinkedIn: p.postOnLinkedIn ?? false,
    postOnNaukriIn: p.postOnNakuri ?? p.postOnNaukri ?? false,
    internal: p.isInternal ?? p.internal ?? true,
  };
}

const EditConsultantManualDrawer = ({ post, onClose, onUpdated }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => postToConsultantEditForm(post));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const { roles, departments } = useRolesAndDepartments();
  const { qualifications, qualificationsLoading } = useQualifications();
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  useEffect(() => {
    setBranchesLoading(true);
    axios
      .get(BRANCHES_API)
      .then((r) => {
        const data = r.data?.data ?? r.data ?? [];
        setBranches(Array.isArray(data) ? data : []);
      })
      .catch(() => setBranches([]))
      .finally(() => setBranchesLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleBudgetChange = (patch) => setForm((p) => ({ ...p, ...patch }));

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.consultancyJobTitle.trim()) e.consultancyJobTitle = "Required";
      if (!form.jobDescription.trim()) e.jobDescription = "Required";
      if (!form.noOfVacancy) e.noOfVacancy = "Required";
      if (!form.departmentId) e.departmentId = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.put(
        `${CONSULTANT_API}/publish/${post.consultancyJobPostId}`,
        {
          consultancyJobTitle: form.consultancyJobTitle,
          jobDescription: form.jobDescription,
          roleDetails: form.roleDetails || undefined,
          roleId: form.roleId ? Number(form.roleId) : undefined,
          departmentId: form.departmentId
            ? Number(form.departmentId)
            : undefined,
          branchId: form.branchId ? Number(form.branchId) : undefined,
          budget: form.budget ? Number(form.budget) : undefined,
          budgetType: form.budgetType?.toUpperCase(),
          salaryType: form.salaryType,
          experience: form.experience || undefined,
          noOfVacancy: Number(form.noOfVacancy),
          skills: form.skills || undefined,
          // ✅ Send qualificationIds instead of qualificationType/qualification
          qualificationIds:
            form.qualificationIds.length > 0
              ? form.qualificationIds
              : undefined,
          expectedClosingTime: form.expectedClosingTime
            ? new Date(form.expectedClosingTime).toISOString()
            : undefined,
          shareToCandidates: form.shareToCandidates,
          postOnLinkedIn: form.postOnLinkedIn,
          postOnNaukriIn: form.postOnNaukriIn,
          isInternal: form.internal,
        },
      );
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update job post.");
    } finally {
      setSubmitting(false);
    }
  };

  const stepJobDetails = (
    <>
      <div className="jp-section-label">Job Details</div>
      <div className="jp-grid-2">
        <F label="Role">
          <select
            className="jp-input"
            name="roleId"
            value={form.roleId}
            onChange={(e) => {
              const r = roles.find((x) => String(x.roleId) === e.target.value);
              setForm((p) => ({
                ...p,
                roleId: e.target.value,
                roleDetails: r?.roleName || p.roleDetails,
                consultancyJobTitle: r?.roleName || p.consultancyJobTitle,
              }));
            }}
          >
            <option value="">Select Role</option>
            {roles.map((r) => (
              <option key={r.roleId} value={r.roleId}>
                {r.roleName}
              </option>
            ))}
          </select>
        </F>
        <F label="Job Title" required error={errors.consultancyJobTitle}>
          <input
            className={`jp-input${errors.consultancyJobTitle ? " jp-input--error" : ""}`}
            name="consultancyJobTitle"
            value={form.consultancyJobTitle}
            onChange={handleChange}
            placeholder="e.g. Sales Executive"
          />
        </F>
        <F label="No. of Positions" required error={errors.noOfVacancy}>
          <input
            className={`jp-input${errors.noOfVacancy ? " jp-input--error" : ""}`}
            type="number"
            min="1"
            name="noOfVacancy"
            value={form.noOfVacancy}
            onChange={handleChange}
          />
        </F>
        <F label="Department" required error={errors.departmentId}>
          <select
            className={`jp-input${errors.departmentId ? " jp-input--error" : ""}`}
            name="departmentId"
            value={form.departmentId}
            onChange={(e) => {
              const d = departments.find(
                (x) => String(x.departmentId) === e.target.value,
              );
              setForm((p) => ({
                ...p,
                departmentId: e.target.value,
                department: d?.departmentName || "",
              }));
              if (errors.departmentId)
                setErrors((prev) => ({ ...prev, departmentId: "" }));
            }}
          >
            <option value="">Select Department</option>
            {departments.map((d) => (
              <option key={d.departmentId} value={d.departmentId}>
                {d.departmentName}
              </option>
            ))}
          </select>
        </F>
      </div>

      <div className="jp-grid">
        <F label="Experience">
          <ExperienceCards value={form.experience} onChange={handleChange} />
        </F>
        <F label="Expected Closing Date">
          <DateTimePicker
            name="expectedClosingTime"
            value={form.expectedClosingTime}
            onChange={handleChange}
          />
        </F>
      </div>

      <div className="jp-grid-1">
        <F label="Budget">
          <BudgetInput
            value={form.budget}
            display={form.budgetDisplay}
            budgetType={form.budgetType}
            salaryType={form.salaryType}
            onChange={handleBudgetChange}
          />
        </F>
      </div>

      <div className="jp-grid-2">
        <F label="Branch">
          <select
            className="jp-input"
            name="branchId"
            value={form.branchId}
            onChange={handleChange}
            disabled={branchesLoading}
          >
            <option value="">
              {branchesLoading ? "Loading branches…" : "Select Branch"}
            </option>
            {branches.map((b) => (
              <option key={b.branchId} value={b.branchId}>
                {b.branchName}
                {b.branchAddress
                  ? ` — ${b.branchAddress.city || ""}, ${b.branchAddress.state || ""}`
                  : ""}
              </option>
            ))}
          </select>
        </F>
        <F label="Skills" hint="Comma-separated">
          <input
            className="jp-input"
            name="skills"
            value={form.skills}
            onChange={handleChange}
            placeholder="e.g. Communication, Sales, Excel"
          />
        </F>
      </div>

      {/* ✅ Replaced two qualification dropdowns with multi-select */}
      <F label="Qualifications">
        <QualificationMultiSelect
          qualifications={qualifications}
          qualificationsLoading={qualificationsLoading}
          selectedIds={form.qualificationIds}
          onChange={(ids) => setForm((p) => ({ ...p, qualificationIds: ids }))}
        />
      </F>

      <div style={{ marginTop: 16 }}>
        <F label="Job Description" required error={errors.jobDescription}>
          <textarea
            className={`jp-input jp-input--textarea${errors.jobDescription ? " jp-input--error" : ""}`}
            name="jobDescription"
            value={form.jobDescription}
            onChange={handleChange}
            rows={4}
            placeholder="Describe the role..."
          />
        </F>
      </div>
    </>
  );

  const stepPublishing = (
    <>
      <div className="jp-section-label">Publishing Options</div>
      <p className="jp-step-hint">
        Choose where this job post should be published.
      </p>
      <div className="jp-toggles">
        <Toggle
          label="Internal (visible inside platform)"
          name="internal"
          checked={form.internal}
          onChange={handleChange}
          icon="🏢"
        />
        <Toggle
          label="Share to Candidates"
          name="shareToCandidates"
          checked={form.shareToCandidates}
          onChange={handleChange}
          icon="🌐"
        />
        <button
          type="button"
          className="jp-share-btn jp-share-btn--disabled"
          disabled
        >
          Post to LinkedIn<span className="jp-beta-tag">Coming Soon</span>
        </button>
        <button
          type="button"
          className="jp-share-btn jp-share-btn--disabled"
          disabled
        >
          Post to Naukri<span className="jp-beta-tag">Coming Soon</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer drawer--wide">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">Edit Manual Job Post</h2>
            <p className="drawer__sub">
              {post.consultancyJobTitle || post.roleName || "Job Post"} · #
              {post.jobPostCode} &nbsp;
              <span style={{ opacity: 0.6 }}>
                — Step {step + 1} of {EDIT_CONSULTANT_MANUAL_STEPS.length}:{" "}
                {EDIT_CONSULTANT_MANUAL_STEPS[step]}
              </span>
            </p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="drawer__stepper">
          {EDIT_CONSULTANT_MANUAL_STEPS.map((label, i) => (
            <div
              key={label}
              className={`drawer__step${i === step ? " drawer__step--active" : ""}${i < step ? " drawer__step--done" : ""}`}
            >
              <div className="drawer__step-dot" />
              <span>{label}</span>
            </div>
          ))}
        </div>
        <div className="drawer__body">
          {step === 0 ? stepJobDetails : stepPublishing}
        </div>
        <div className="drawer__footer">
          {step > 0 ? (
            <button
              className="cv-btn cv-btn--ghost"
              onClick={() => setStep((s) => s - 1)}
            >
              ← Back
            </button>
          ) : (
            <button className="cv-btn cv-btn--ghost" onClick={onClose}>
              Cancel
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < EDIT_CONSULTANT_MANUAL_STEPS.length - 1 ? (
            <button
              className="cv-btn cv-btn--primary"
              onClick={() => {
                if (validate()) setStep((s) => s + 1);
              }}
            >
              Continue →
            </button>
          ) : (
            <button
              className="cv-btn cv-btn--submit"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <span className="cv-btn__spinner" />
              ) : (
                "Save Changes"
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  CreateConsultantJobPostDrawer
// ─────────────────────────────────────────────────────────────────────────────
const CONSULTANT_CREATE_STEPS = ["Select Requirement", "Publishing Options"];
const CONSULTANT_MANUAL_STEPS = ["Job Details", "Publishing Options"];

const CreateConsultantJobPostDrawer = ({
  onClose,
  onCreated,
  prefillRequirementId,
}) => {
  const [createMode, setCreateMode] = useState("requirement");
  return createMode === "manual" ? (
    <CreateConsultantManualDrawer
      onClose={onClose}
      onCreated={onCreated}
      createMode={createMode}
      setCreateMode={setCreateMode}
    />
  ) : (
    <CreateConsultantRequirementDrawer
      onClose={onClose}
      onCreated={onCreated}
      createMode={createMode}
      setCreateMode={setCreateMode}
      prefillRequirementId={prefillRequirementId}
    />
  );
};

// ── From-Requirement drawer ──────────────────────────────────────────────────
const CreateConsultantRequirementDrawer = ({
  onClose,
  onCreated,
  createMode,
  setCreateMode,
  prefillRequirementId,
}) => {
  const [step, setStep] = useState(0);
  const [requirements, setReqs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    candidateRequirementId: "",
    isInternal: true,
    postOnLinkedIn: false,
    postOnNakuri: false,
    shareToCandidates: false,
    status: "OPEN",
  });

  const selectedReq = useMemo(
    () =>
      requirements.find(
        (r) =>
          String(r.candidateRequirementId) ===
          String(form.candidateRequirementId),
      ),
    [requirements, form.candidateRequirementId],
  );

  useEffect(() => {
    axios
      .get(CONSULTANT_REQ)
      .then((r) => {
        const allRequirements = r.data?.data || [];
        const availableRequirements = allRequirements.filter((req) => {
          const isActive = req.status?.toLowerCase() === "active";
          const isNotPosted = req.consultancyJobPostId === null;
          return isActive && isNotPosted;
        });
        setReqs(availableRequirements);
        if (prefillRequirementId) {
          const exists = availableRequirements.find(
            (req) =>
              String(req.candidateRequirementId) ===
              String(prefillRequirementId),
          );
          if (exists)
            setForm((p) => ({
              ...p,
              candidateRequirementId: String(prefillRequirementId),
            }));
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        toast.error("Failed to load company requirement");
      });
  }, [prefillRequirementId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (step === 0 && !form.candidateRequirementId)
      e.candidateRequirementId = "Select a requirement";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${CONSULTANT_API}/create`, {
        candidateRequirementId: Number(form.candidateRequirementId),
        isInternal: form.isInternal,
        postOnLinkedIn: form.postOnLinkedIn,
        postOnNakuri: form.postOnNakuri,
        shareToCandidates: form.shareToCandidates,
        status: form.status,
      });
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to post a job.");
    } finally {
      setSubmitting(false);
    }
  };

  const step0 = (
    <>
      <div className="jp-section-label">company requirement</div>
      <p className="jp-step-hint">
        Select the client's company requirement to post a job from.
      </p>
      <F
        label="company requirement"
        required
        error={errors.candidateRequirementId}
      >
        <select
          className="jp-input"
          value={form.candidateRequirementId || ""}
          disabled={requirements.length === 0}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              candidateRequirementId: e.target.value,
            }))
          }
          style={{
            cursor: requirements.length === 0 ? "not-allowed" : "pointer",
            opacity: requirements.length === 0 ? 0.7 : 1,
          }}
        >
          {requirements.length === 0 ? (
            <option value="">
              No company requirement have been created yet
            </option>
          ) : (
            <>
              <option value="">Select requirement</option>
              {requirements.map((r) => (
                <option
                  key={r.candidateRequirementId}
                  value={r.candidateRequirementId}
                >
                  {r.requirementName || r.jobTitle}
                </option>
              ))}
            </>
          )}
        </select>
      </F>

      {selectedReq && (
        <div className="jp-req-preview">
          {[
            ["Role", selectedReq.role],
            ["Company", selectedReq.recruitmentCompanyName],
            ["Module", selectedReq.moduleHistoryName],
            ["Positions", selectedReq.noOfVacancy],
            ["Experience", selectedReq.experience],
            [
              "Budget",
              selectedReq.budget > 0
                ? `₹${Number(selectedReq.budget).toLocaleString()}`
                : null,
            ],
            ["Job Code", selectedReq.jobCode],
            // ✅ Changed from "Qualification Type" to "Qualification"
            [
              "Qualification",
              Array.isArray(selectedReq.qualifications) &&
              selectedReq.qualifications.length > 0
                ? selectedReq.qualifications
                    .map((q) => q.qualificationName || q)
                    .join(", ")
                : selectedReq.qualification || null,
            ],
          ]
            .filter(([, v]) => v)
            .map(([label, val]) => (
              <div className="jp-req-preview__row" key={label}>
                <div className="jp-req-preview__label">{label}</div>
                <div className="jp-req-preview__val">{val}</div>
              </div>
            ))}
          {selectedReq.jobDescription && (
            <div className="jp-req-preview__row">
              <div className="jp-req-preview__label">Description</div>
              <div
                className="jp-req-preview__val"
                style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}
              >
                {selectedReq.jobDescription.slice(0, 200)}
                {selectedReq.jobDescription.length > 200 ? "…" : ""}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  const step1 = (
    <>
      <div className="jp-section-label">Publishing Options</div>
      <p className="jp-step-hint">
        Choose where this job post should be published.
      </p>
      <div className="jp-toggles">
        <Toggle
          label="Internal (visible inside platform)"
          name="isInternal"
          checked={form.isInternal}
          onChange={handleChange}
          icon="🏢"
        />
        <Toggle
          label="Share to Candidates"
          name="shareToCandidates"
          checked={form.shareToCandidates}
          onChange={handleChange}
          icon="🌐"
        />
        <Toggle
          label="Post to LinkedIn"
          name="postOnLinkedIn"
          checked={form.postOnLinkedIn}
          onChange={handleChange}
          icon="💼"
        />
        <Toggle
          label="Post to Naukri"
          name="postOnNakuri"
          checked={form.postOnNakuri}
          onChange={handleChange}
          icon="📋"
        />
      </div>
      <div className="jp-section-label" style={{ marginTop: 20 }}>
        Status
      </div>
      <div className="jp-status-row">
        {["OPEN", "DRAFT"].map((s) => (
          <button
            key={s}
            type="button"
            className={`jp-status-pill${form.status === s ? " jp-status-pill--active" : ""}`}
            onClick={() => setForm((p) => ({ ...p, status: s }))}
          >
            {s === "OPEN" ? "🟢" : "📝"} {s}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer drawer--wide">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title"> Company Requirementt</h2>
            <p className="drawer__sub">
              Step {step + 1} of {CONSULTANT_CREATE_STEPS.length} —{" "}
              {CONSULTANT_CREATE_STEPS[step]}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ fontWeight: 600 }}>Source:</span>

              <div className="jp-mode-toggle">
                <button
                  type="button"
                  className={`jp-mode-toggle__btn${createMode === "requirement" ? " jp-mode-toggle__btn--active" : ""}`}
                  onClick={() => setCreateMode("requirement")}
                >
                  Company Requirement
                </button>

                <button
                  type="button"
                  className={`jp-mode-toggle__btn${createMode === "manual" ? " jp-mode-toggle__btn--active" : ""}`}
                  onClick={() => setCreateMode("manual")}
                >
                  My Job Post
                </button>
              </div>
            </div>
            <button className="drawer__close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="drawer__stepper">
          {CONSULTANT_CREATE_STEPS.map((label, i) => (
            <div
              key={label}
              className={`drawer__step${i === step ? " drawer__step--active" : ""}${i < step ? " drawer__step--done" : ""}`}
            >
              <div className="drawer__step-dot" />
              <span>{label}</span>
            </div>
          ))}
        </div>
        <div className="drawer__body">{step === 0 ? step0 : step1}</div>
        <div className="drawer__footer">
          {step > 0 ? (
            <button
              className="cv-btn cv-btn--ghost"
              onClick={() => setStep((s) => s - 1)}
            >
              ← Back
            </button>
          ) : (
            <button className="cv-btn cv-btn--ghost" onClick={onClose}>
              Cancel
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < CONSULTANT_CREATE_STEPS.length - 1 ? (
            <button
              className="cv-btn cv-btn--primary"
              onClick={() => {
                if (validate()) setStep((s) => s + 1);
              }}
            >
              Continue →
            </button>
          ) : (
            <button
              className="cv-btn cv-btn--submit"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? <span className="cv-btn__spinner" /> : "Post a Job"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ── Manual Creation drawer ────────────────────────────────────────────────────
const emptyManualForm = {
  consultancyJobTitle: "",
  jobDescription: "",
  roleDetails: "",
  roleId: "",
  departmentId: "",
  department: "",
  branchId: "",
  budget: "",
  budgetDisplay: "",
  budgetType: "annual",
  salaryType: "CTC",
  experience: "",
  noOfVacancy: 1,
  skills: "",
  qualificationIds: [],
  expectedClosingTime: "",
  isInternal: true,
  shareToCandidates: false,
  postOnLinkedIn: false,
  postOnNakuri: false,
  status: "OPEN",
};

const CreateConsultantManualDrawer = ({
  onClose,
  onCreated,
  createMode,
  setCreateMode,
}) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyManualForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const { roles, departments } = useRolesAndDepartments();
  const { qualifications, qualificationsLoading } = useQualifications();
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  useEffect(() => {
    setBranchesLoading(true);
    axios
      .get(BRANCHES_API)
      .then((r) => {
        const data = r.data?.data ?? r.data ?? [];
        setBranches(Array.isArray(data) ? data : []);
      })
      .catch(() => setBranches([]))
      .finally(() => setBranchesLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleBudgetChange = (patch) => setForm((p) => ({ ...p, ...patch }));

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.consultancyJobTitle.trim()) e.consultancyJobTitle = "Required";
      if (!form.jobDescription.trim()) e.jobDescription = "Required";
      if (!form.noOfVacancy) e.noOfVacancy = "Required";
      if (!form.departmentId) e.departmentId = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${CONSULTANT_API}/create`, {
        candidateRequirementId: null,
        consultancyJobTitle: form.consultancyJobTitle,
        jobDescription: form.jobDescription,
        roleDetails: form.roleDetails || undefined,
        roleId: form.roleId ? Number(form.roleId) : undefined,
        departmentId: form.departmentId ? Number(form.departmentId) : undefined,
        branchId: form.branchId ? Number(form.branchId) : undefined,
        budget: form.budget ? Number(form.budget) : undefined,
        budgetType: form.budgetType?.toUpperCase(),
        salaryType: form.salaryType,
        experience: form.experience || undefined,
        noOfVacancy: Number(form.noOfVacancy),
        skills: form.skills || undefined,
        // ✅ qualificationIds instead of qualificationType/qualification
        qualificationIds:
          form.qualificationIds.length > 0 ? form.qualificationIds : undefined,
        department: form.department || undefined,
        expectedClosingTime: form.expectedClosingTime
          ? new Date(form.expectedClosingTime).toISOString()
          : undefined,
        isInternal: form.isInternal,
        shareToCandidates: form.shareToCandidates,
        postOnLinkedIn: form.postOnLinkedIn,
        postOnNakuri: form.postOnNakuri,
        status: form.status,
      });
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to post a job.");
    } finally {
      setSubmitting(false);
    }
  };

  const step0 = (
    <>
      <div className="jp-section-label">Job Details</div>
      <div className="jp-grid-2">
        <F label="Role">
          <select
            className="jp-input"
            name="roleId"
            value={form.roleId}
            onChange={(e) => {
              const r = roles.find((x) => String(x.roleId) === e.target.value);
              setForm((p) => ({
                ...p,
                roleId: e.target.value,
                roleDetails: r?.roleName || "",
                consultancyJobTitle: r?.roleName || p.consultancyJobTitle,
              }));
            }}
          >
            <option value="">Select Role</option>
            {roles.map((r) => (
              <option key={r.roleId} value={r.roleId}>
                {r.roleName}
              </option>
            ))}
          </select>
        </F>
        <F label="Job Title" required error={errors.consultancyJobTitle}>
          <input
            className={`jp-input${errors.consultancyJobTitle ? " jp-input--error" : ""}`}
            name="consultancyJobTitle"
            value={form.consultancyJobTitle}
            onChange={handleChange}
            placeholder="e.g. Sales Executive"
          />
        </F>
        <F label="No. of Positions" required error={errors.noOfVacancy}>
          <input
            className={`jp-input${errors.noOfVacancy ? " jp-input--error" : ""}`}
            type="number"
            min="1"
            name="noOfVacancy"
            value={form.noOfVacancy}
            onChange={handleChange}
          />
        </F>
        <F label="Department" required error={errors.departmentId}>
          <select
            className={`jp-input${errors.departmentId ? " jp-input--error" : ""}`}
            name="departmentId"
            value={form.departmentId}
            onChange={(e) => {
              const d = departments.find(
                (x) => String(x.departmentId) === e.target.value,
              );
              setForm((p) => ({
                ...p,
                departmentId: e.target.value,
                department: d?.departmentName || "",
              }));
              if (errors.departmentId)
                setErrors((p) => ({ ...p, departmentId: "" }));
            }}
          >
            <option value="">Select Department</option>
            {departments.map((d) => (
              <option key={d.departmentId} value={d.departmentId}>
                {d.departmentName}
              </option>
            ))}
          </select>
        </F>
      </div>

      <div className="jp-grid">
        <F label="Experience">
          <ExperienceCards value={form.experience} onChange={handleChange} />
        </F>
        <F label="Expected Closing Date">
          <DateTimePicker
            name="expectedClosingTime"
            value={form.expectedClosingTime}
            onChange={handleChange}
          />
        </F>
      </div>

      <div className="jp-grid-2">
        <F label="Branch">
          <select
            className="jp-input"
            name="branchId"
            value={form.branchId}
            onChange={handleChange}
            disabled={branchesLoading}
          >
            <option value="">
              {branchesLoading ? "Loading branches…" : "Select Branch"}
            </option>
            {branches.map((b) => (
              <option key={b.branchId} value={b.branchId}>
                {b.branchName}
                {b.branchAddress
                  ? ` — ${b.branchAddress.city || ""}, ${b.branchAddress.state || ""}`
                  : ""}
              </option>
            ))}
          </select>
        </F>
      </div>

      <div className="jp-grid-1">
        <F label="Budget">
          <BudgetInput
            value={form.budget}
            display={form.budgetDisplay}
            budgetType={form.budgetType}
            salaryType={form.salaryType}
            onChange={handleBudgetChange}
          />
        </F>
      </div>

      <F label="Skills" hint="Comma-separated">
        <input
          className="jp-input"
          name="skills"
          value={form.skills}
          onChange={handleChange}
          placeholder="e.g. Communication, Sales, Excel"
        />
      </F>

      {/* ✅ Replaced two qualification dropdowns with multi-select */}
      <F label="Qualifications">
        <QualificationMultiSelect
          qualifications={qualifications}
          qualificationsLoading={qualificationsLoading}
          selectedIds={form.qualificationIds}
          onChange={(ids) => setForm((p) => ({ ...p, qualificationIds: ids }))}
        />
      </F>

      <div style={{ marginTop: 16 }}>
        <F label="Job Description" required error={errors.jobDescription}>
          <div className="jp-suggest-wrap">
            <textarea
              className={`jp-input jp-textarea${errors.jobDescription ? " jp-input--error" : ""}`}
              rows={5}
              name="jobDescription"
              value={form.jobDescription}
              placeholder="Describe the role..."
              onChange={handleChange}
            />
            <div className="jp-suggestions">
              {JOB_DESCRIPTION_SUGGESTIONS.map((item, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="suggestion-chip"
                  onClick={() => {
                    const exists = form.jobDescription?.includes(item);
                    setForm((prev) => ({
                      ...prev,
                      jobDescription: exists
                        ? prev.jobDescription
                        : prev.jobDescription
                          ? `${prev.jobDescription}\n• ${item}`
                          : `• ${item}`,
                    }));
                  }}
                >
                  + {item}
                </button>
              ))}
            </div>
          </div>
        </F>
      </div>
    </>
  );

  const step1 = (
    <>
      <div className="jp-section-label">Publishing Options</div>
      <p className="jp-step-hint">
        Choose where this job post should be published.
      </p>
      <div className="jp-toggles">
        <Toggle
          label="Internal (visible inside platform)"
          name="isInternal"
          checked={form.isInternal}
          onChange={handleChange}
          icon="🏢"
        />
        <Toggle
          label="Share to Candidates"
          name="shareToCandidates"
          checked={form.shareToCandidates}
          onChange={handleChange}
          icon="🌐"
        />
        <button
          type="button"
          className="jp-share-btn jp-share-btn--disabled"
          disabled
        >
          Post to LinkedIn<span className="jp-beta-tag">Coming Soon</span>
        </button>
        <button
          type="button"
          className="jp-share-btn jp-share-btn--disabled"
          disabled
        >
          Post to Naukri<span className="jp-beta-tag">Coming Soon</span>
        </button>
      </div>
      <div className="jp-section-label" style={{ marginTop: 20 }}>
        Status
      </div>
      <div className="jp-status-row">
        {["OPEN", "DRAFT"].map((s) => (
          <button
            key={s}
            type="button"
            className={`jp-status-pill${form.status === s ? " jp-status-pill--active" : ""}`}
            onClick={() => setForm((p) => ({ ...p, status: s }))}
          >
            {s === "OPEN" ? "🟢" : "📝"} {s}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer drawer--wide">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">Create Manual Job Post</h2>
            <p className="drawer__sub">
              Step {step + 1} of {CONSULTANT_MANUAL_STEPS.length} —{" "}
              {CONSULTANT_MANUAL_STEPS[step]}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontWeight: 600 }}>Source:</span>

            <div className="jp-mode-toggle">
              <button
                type="button"
                className={`jp-mode-toggle__btn${createMode === "requirement" ? " jp-mode-toggle__btn--active" : ""}`}
                onClick={() => setCreateMode("requirement")}
              >
                Company Requirement
              </button>
              <button
                type="button"
                className={`jp-mode-toggle__btn${createMode === "manual" ? " jp-mode-toggle__btn--active" : ""}`}
                onClick={() => setCreateMode("manual")}
              >
                My Job Post{" "}
              </button>
            </div>
            <button className="drawer__close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="drawer__stepper">
          {CONSULTANT_MANUAL_STEPS.map((label, i) => (
            <div
              key={label}
              className={`drawer__step${i === step ? " drawer__step--active" : ""}${i < step ? " drawer__step--done" : ""}`}
            >
              <div className="drawer__step-dot" />
              <span>{label}</span>
            </div>
          ))}
        </div>
        <div className="drawer__body">{step === 0 ? step0 : step1}</div>
        <div className="drawer__footer">
          {step > 0 ? (
            <button
              className="cv-btn cv-btn--ghost"
              onClick={() => setStep((s) => s - 1)}
            >
              ← Back
            </button>
          ) : (
            <button className="cv-btn cv-btn--ghost" onClick={onClose}>
              Cancel
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < CONSULTANT_MANUAL_STEPS.length - 1 ? (
            <button
              className="cv-btn cv-btn--primary"
              onClick={() => {
                if (validate()) setStep((s) => s + 1);
              }}
            >
              Continue →
            </button>
          ) : (
            <button
              className="cv-btn cv-btn--submit"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? <span className="cv-btn__spinner" /> : "Post a Job"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  PublishConsultantJobPostDrawer
// ─────────────────────────────────────────────────────────────────────────────
const PublishConsultantJobPostDrawer = ({ post, onClose, onDone }) => {
  const [form, setForm] = useState({
    sharedToCandidates: post.sharedToCandidates || false,
    postOnLinkedIn: post.postOnLinkedIn || false,
    postOnLinkedInUrl: post.postOnLinkedInUrl || "",
    postOnNaukriIn: post.postOnNakuri || false,
    postOnNaukriUrl: post.postOnNakuriUrl || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.put(
        `${CONSULTANT_API}/publish/${post.consultancyJobPostId}`,
        {
          jobPostId: post.consultancyJobPostId,
          sharedToCandidates: form.sharedToCandidates,
          postOnLinkedIn: form.postOnLinkedIn,
          postOnLinkedInUrl: form.postOnLinkedInUrl || null,
          postOnNaukriIn: form.postOnNaukriIn,
          postOnNaukriUrl: form.postOnNaukriUrl || null,
        },
      );
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">Publishing Options</h2>
            <p className="drawer__sub">
              {post.consultancyJobTitle || post.roleName || "Job Post"} · #
              {post.jobPostCode}
            </p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="drawer__body">
          <div className="jp-section-label">Sharing</div>
          <div className="jp-toggles">
            <Toggle
              label="Share to Candidates Portal"
              name="sharedToCandidates"
              checked={form.sharedToCandidates}
              onChange={handleChange}
              icon="🌐"
            />
            <button
              type="button"
              className="jp-share-btn jp-share-btn--disabled"
              disabled
            >
              Post to LinkedIn<span className="jp-beta-tag">Coming Soon</span>
            </button>
            <button
              type="button"
              className="jp-share-btn jp-share-btn--disabled"
              disabled
            >
              Post to Naukri<span className="jp-beta-tag">Coming Soon</span>
            </button>
          </div>
        </div>
        <div className="drawer__footer">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="cv-btn cv-btn--submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <span className="cv-btn__spinner" /> : "Save Changes"}
          </button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  EditCompanyJobPostDrawer
// ─────────────────────────────────────────────────────────────────────────────
const EDIT_STEPS = ["Job Details", "Sharing & Flow"];

function postToEditForm(p) {
  return {
    jobTitle: p.jobTitle || "",
    jobDescription: p.jobDescription || "",
    responsibilities: p.responsibilities || "",
    skills: p.skills || "",
    desiredProfile: p.desiredProfile || "",
    experience: p.experience || "",
    employmentType: p.employmentType || "PERMANENT",
    qualificationIds: extractQualificationIds(p),
    expectedClosingTime: p.expectedClosingTime
      ? p.expectedClosingTime.slice(0, 16)
      : "",
    budget: p.budget != null ? String(p.budget) : "",
    budgetDisplay: p.budget ? toIndianFormat(p.budget) : "",
    budgetType: p.budgetType ? p.budgetType.toUpperCase() : "ANNUAL",
    salaryType: p.salaryType ? p.salaryType.toUpperCase() : "CTC",
    showBudget: p.showBudget ?? true,
    applicationClosingDate: p.applicationClosingDate
      ? p.applicationClosingDate.slice(0, 16)
      : "",
    noOfVacancy: p.noOfVacancy || 1,
    roleId: p.roleId ? String(p.roleId) : "",
    roleName: p.roleName || "",
    departmentId: p.departmentId ? String(p.departmentId) : "",
    department: p.department || p.departmentName || "",
    branchId: p.branchId ? String(p.branchId) : "",
    shareToCandidates: p.shareToCandidates ?? true,
    postToLinkedIn:
      p.postOnLinkedIn != null
        ? !!p.postOnLinkedIn
        : (p.postToLinkedIn ?? false),
    postToNaukri:
      p.postOnNakuri != null ? !!p.postOnNakuri : (p.postToNaukri ?? false),
    shareToConsultancy: !!(
      p.shareToConsultancy || p.consultancySharedResponseDTOList?.length > 0
    ),
    selectedConnections: Object.fromEntries(
      (p.consultancySharedResponseDTOList || []).map((c) => [
        String(c.connectionId), // ✅ force string key
        { connectionId: c.connectionId, remark: c.remark || "" },
      ]),
    ),
    hiringFlowMode: "existing",
    hiringFlowId: p.hiringFlowId
      ? String(p.hiringFlowId)
      : p.hiringFlow?.hiringFlowId
        ? String(p.hiringFlow.hiringFlowId)
        : "",
    hiringFlowName: p.hiringFlowName || p.hiringFlow?.hiringFlowName || "",
    rounds: p.hiringFlow?.rounds
      ? p.hiringFlow.rounds.map((r) => ({
          roundName: r.roundName || "",
          sequence: r.sequence,
          interviewMode: (r.interviewMode || "ONLINE")
            .toString()
            .trim()
            .toUpperCase(),
          employeeIds: r.employeeIds || [],
          initial: r.initial || false,
          final: r.final || false,
          description: r.description || "",
        }))
      : [
          {
            roundName: "",
            sequence: 1,
            initial: true,
            final: false,
            interviewMode: "ONLINE",
            employeeIds: [],
          },
        ],
  };
}

const EditCompanyJobPostDrawer = ({ post, onClose, onUpdated }) => {
  const [form, setForm] = useState(() => postToEditForm(post));
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [consultancies, setConsultancies] = useState([]);
  const [consLoading, setConsLoading] = useState(false);
  const [consLoaded, setConsLoaded] = useState(false);
  const [titleAutoFilled, setTitleAutoFilled] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const { roles, departments } = useRolesAndDepartments();
  const { hiringFlows, hiringFlowsLoading } = useHiringFlows();
  const { qualifications, qualificationsLoading } = useQualifications();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "roleId" && titleAutoFilled) {
      const role = roles.find((r) => String(r.roleId) === value);
      setForm((p) => ({
        ...p,
        roleId: value,
        roleName: role?.roleName || "",
        jobTitle: role ? role.roleName : p.jobTitle,
      }));
      if (errors.roleId) setErrors((p) => ({ ...p, roleId: "" }));
      return;
    }
    if (name === "jobTitle") setTitleAutoFilled(false);
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  // Merge active consultancies with any already-shared connections that
  // no longer show up in the active list (e.g. connection status changed).
  // Falls back to the info stored on the post itself so names still display.
  const displayConsultancies = useMemo(() => {
    const activeIds = new Set(
      consultancies.map((c) => String(c.connectionId)), // ✅ string
    );
    const staleShared = (post.consultancySharedResponseDTOList || [])
      .filter(
        (c) =>
          form.selectedConnections[String(c.connectionId)] && // ✅ string lookup
          !activeIds.has(String(c.connectionId)),
      )
      .map((c) => ({
        connectionId: c.connectionId,
        companyName: c.connectionName,
        companyLogo: null,
        moduleName: null,
        isStale: true,
      }));
    return [...consultancies, ...staleShared];
  }, [
    consultancies,
    post.consultancySharedResponseDTOList,
    form.selectedConnections,
  ]);

  const handleBudgetChange = (patch) => setForm((p) => ({ ...p, ...patch }));

  const loadConsultancies = () => {
    if (consLoaded) return;

    setConsLoading(true);

    axios
      .get(CONNECTIONS_API)
      .then((r) => {
        const all = r.data.data || [];

        setConsultancies(
          all.filter((con) => {
            const agreementStatus = normalizeConnectionStatus(
              con.moduleAgreementStatus,
            );

            const isConnected =
              agreementStatus === "REQUEST_ACCEPTED" ||
              agreementStatus === "ACCEPTED";

            const tcSettled = ["SENT_ACCEPTED", "RECEIVED_ACCEPTED"].includes(
              normalizeConnectionStatus(con.senderDocumentStatus),
            );

            return isConnected && tcSettled;
          }),
        );

        setConsLoaded(true);
      })
      .catch(console.error)
      .finally(() => setConsLoading(false));
  };

  // ✅ FIX: fetch consultancies immediately if this post was already
  // shared to connections when the drawer opened — otherwise the
  // checkbox shows checked but the names never load, since the
  // original fetch only fired from the Toggle's onChange handler.
  useEffect(() => {
    if (form.shareToConsultancy) {
      loadConsultancies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setBranchesLoading(true);
    axios
      .get(BRANCHES_API)
      .then((res) => setBranches(res.data.data || []))
      .catch(console.error)
      .finally(() => setBranchesLoading(false));
  }, []);

  const toggleConnection = (connectionId) => {
    const key = String(connectionId);
    setForm((p) => {
      const conns = { ...p.selectedConnections };
      if (conns[key]) delete conns[key];
      else conns[key] = { connectionId, remark: "" };
      return { ...p, selectedConnections: conns };
    });
  };

  const updateRemark = (connectionId, remark) => {
    const key = String(connectionId);
    setForm((p) => ({
      ...p,
      selectedConnections: {
        ...p.selectedConnections,
        [key]: { ...p.selectedConnections[key], remark },
      },
    }));
  };

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.jobTitle.trim()) e.jobTitle = "Required";
      if (!form.jobDescription.trim()) e.jobDescription = "Required";
      if (!form.noOfVacancy) e.noOfVacancy = "Required";
    }
    if (step === 1) {
      if (form.hiringFlowMode === "existing" && !form.hiringFlowId)
        e.hiringFlowId = "Select a hiring flow";
      if (form.hiringFlowMode === "new") {
        if (!form.hiringFlowName.trim()) e.hiringFlowName = "Required";
        form.rounds.forEach((r, i) => {
          if (r.roundName.trim() === "" && i < form.rounds.length - 1)
            e[`round_${i}`] = "Required";
        });
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const cleanRounds = form.rounds
        .filter((r) => r.roundName.trim() !== "")
        .map((r, idx) => ({
          roundName: r.roundName.trim(),
          sequence: r.sequence || idx + 1,
          interviewMode: (r.interviewMode || "ONLINE")
            .toString()
            .trim()
            .toUpperCase(),
          employeeIds: r.employeeIds || [],
          initial: !!r.initial,
          final: !!r.final,
          description: r.description || "",
        }));

      const hiringFlowPart =
        form.hiringFlowMode === "existing"
          ? {
              hiringFlowId:
                Number(form.hiringFlowId) || Number(post.hiringFlowId),
            }
          : {
              hiringFlow: {
                hiringFlowName: form.hiringFlowName?.trim(),
                rounds: cleanRounds,
              },
            };

      const payload = {
        jobTitle: form.jobTitle?.trim(),
        jobDescription: form.jobDescription || "",
        responsibilities: form.responsibilities || "",
        skills: form.skills || "",
        desiredProfile: form.desiredProfile || "",
        experience: form.experience || "",
        employmentType: normalizeEmploymentType(form.employmentType),

        qualificationIds:
          form.qualificationIds.length > 0 ? form.qualificationIds : undefined,

        expectedClosingTime: form.expectedClosingTime
          ? new Date(form.expectedClosingTime).toISOString()
          : undefined,

        budget: form.budget !== "" ? Number(form.budget) : undefined,

        budgetType: form.budgetType ? form.budgetType.toUpperCase() : undefined,

        salaryType: form.salaryType ? form.salaryType.toUpperCase() : undefined,

        showBudget: !!form.showBudget,

        applicationClosingDate: form.applicationClosingDate
          ? new Date(form.applicationClosingDate).toISOString()
          : undefined,

        noOfVacancy: Number(form.noOfVacancy || 0),

        roleId: form.roleId ? Number(form.roleId) : undefined,

        departmentId: form.departmentId ? Number(form.departmentId) : undefined,

        branchId: form.branchId ? Number(form.branchId) : undefined,

        shareToCandidates: !!form.shareToCandidates,
        postToLinkedIn: !!form.postToLinkedIn,
        postToNaukri: !!form.postToNaukri,
        shareToConsultancy: !!form.shareToConsultancy,

        jobPostSharedHistoryRequestDTOS: form.shareToConsultancy
          ? Object.values(form.selectedConnections || {}).map(
              ({ connectionId, remark }) => ({
                connectionId: Number(connectionId),
                remark: remark || "",
              }),
            )
          : [],

        ...hiringFlowPart,
      };

      await axios.put(`${COMPANY_API}/${post.jobPostId}`, payload);
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update job post.");
    } finally {
      setLoading(false);
    }
  };

  const stepJobDetails = (
    <>
      <div className="jp-section-label">Basic Information</div>
      <div className="jp-grid-3">
        <F label="Role">
          <select
            className="jp-input"
            name="roleId"
            value={form.roleId}
            onChange={(e) => {
              const r = roles.find((x) => String(x.roleId) === e.target.value);
              setForm((p) => ({
                ...p,
                roleId: e.target.value,
                roleName: r?.roleName || "",
              }));
            }}
          >
            <option value="">Select Role</option>
            {roles.map((r) => (
              <option key={r.roleId} value={r.roleId}>
                {r.roleName}
              </option>
            ))}
          </select>
        </F>
        <F label="Job Title" required error={errors.jobTitle}>
          <input
            className={`jp-input${errors.jobTitle ? " jp-input--error" : ""}`}
            name="jobTitle"
            value={form.jobTitle}
            onChange={handleChange}
            placeholder="e.g. Sales Executive"
          />
        </F>
        <F label="No. of Positions" required error={errors.noOfVacancy}>
          <input
            className={`jp-input${errors.noOfVacancy ? " jp-input--error" : ""}`}
            type="number"
            min="1"
            name="noOfVacancy"
            value={form.noOfVacancy}
            onChange={handleChange}
          />
        </F>
        <F label="Employment Type">
          <select
            className="jp-input"
            name="employmentType"
            value={form.employmentType}
            onChange={handleChange}
          >
            <option value="PERMANENT">Permanent</option>
            <option value="CONTRACT">Contract</option>
            <option value="PART_TIME">Part Time</option>
            <option value="INTERN">Intern</option>
          </select>
        </F>
        <F label="Department">
          <select
            className="jp-input"
            name="departmentId"
            value={form.departmentId}
            onChange={(e) => {
              const d = departments.find(
                (x) => String(x.departmentId) === e.target.value,
              );
              setForm((p) => ({
                ...p,
                departmentId: e.target.value,
                department: d?.departmentName || "",
              }));
            }}
          >
            <option value="">Select Department</option>
            {departments.map((d) => (
              <option key={d.departmentId} value={d.departmentId}>
                {d.departmentName}
              </option>
            ))}
          </select>
        </F>
        <F label="Branch">
          <select
            className="jp-input"
            name="branchId"
            value={form.branchId || ""}
            onChange={handleChange}
            disabled={branchesLoading}
          >
            <option value="">
              {branchesLoading ? "Loading branches…" : "Select Branch"}
            </option>
            {branches.map((b) => (
              <option key={b.branchId} value={b.branchId}>
                {b.branchName}
              </option>
            ))}
          </select>
        </F>
      </div>
      <div className="jp-grid">
        <F label="Experience">
          <ExperienceCards value={form.experience} onChange={handleChange} />
        </F>
        <F label="Expected Closing Date">
          <DateTimePicker
            name="expectedClosingTime"
            value={form.expectedClosingTime}
            onChange={handleChange}
          />
        </F>
        <F label="Application Closing Date">
          <DateTimePicker
            name="applicationClosingDate"
            value={form.applicationClosingDate}
            onChange={handleChange}
          />
        </F>
      </div>
      <div className="jp-grid">
        <F label="Budget">
          <BudgetInput
            value={form.budget}
            display={form.budgetDisplay}
            budgetType={form.budgetType}
            salaryType={form.salaryType}
            onChange={handleBudgetChange}
          />
        </F>
        <Toggle
          label="Show budget to candidates"
          name="showBudget"
          checked={form.showBudget}
          onChange={handleChange}
          icon="💰"
        />
      </div>
      <div className="jp-section-label" style={{ marginTop: 20 }}>
        Job Content
      </div>
      <F label="Job Description" required error={errors.jobDescription}>
        <textarea
          className={`jp-input jp-input--textarea${errors.jobDescription ? " jp-input--error" : ""}`}
          name="jobDescription"
          value={form.jobDescription}
          onChange={handleChange}
          rows={4}
          placeholder="Describe the role..."
        />
        <div className="cv-suggestions">
          {JOB_DESCRIPTION_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className="suggestion-chip"
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  jobDescription: p.jobDescription
                    ? `${p.jobDescription}\n• ${s}`
                    : `• ${s}`,
                }))
              }
            >
              {s}
            </button>
          ))}
        </div>
      </F>
      <F label="Responsibilities">
        <textarea
          className="jp-input jp-input--textarea"
          name="responsibilities"
          value={form.responsibilities}
          onChange={handleChange}
          rows={3}
          placeholder="Key responsibilities..."
        />
        <div className="cv-suggestions">
          {RESPONSIBILITY_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className="suggestion-chip"
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  responsibilities: p.responsibilities
                    ? `${p.responsibilities}\n• ${s}`
                    : `• ${s}`,
                }))
              }
            >
              {s}
            </button>
          ))}
        </div>
      </F>
      <F label="Desired Profile">
        <textarea
          className="jp-input jp-input--textarea"
          name="desiredProfile"
          value={form.desiredProfile}
          onChange={handleChange}
          rows={3}
          placeholder="Ideal candidate profile..."
        />
        <div className="cv-suggestions">
          {DESIRED_PROFILE_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className="suggestion-chip"
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  desiredProfile: p.desiredProfile
                    ? `${p.desiredProfile}\n• ${s}`
                    : `• ${s}`,
                }))
              }
            >
              {s}
            </button>
          ))}
        </div>
      </F>
      <div className="jp-grid-2" style={{ marginTop: 14 }}>
        <F label="Skills" hint="Comma-separated">
          <input
            className="jp-input"
            name="skills"
            value={form.skills}
            onChange={handleChange}
            placeholder="e.g. Communication, Sales, Excel"
          />
        </F>
      </div>
      {/* ✅ Replaced two qualification dropdowns with multi-select */}
      <F label="Qualifications" style={{ marginTop: 14 }}>
        <QualificationMultiSelect
          qualifications={qualifications}
          qualificationsLoading={qualificationsLoading}
          selectedIds={form.qualificationIds}
          onChange={(ids) => setForm((p) => ({ ...p, qualificationIds: ids }))}
        />
      </F>
    </>
  );

  const stepSharing = (
    <SharingAndFlowStep
      form={form}
      errors={errors}
      handleChange={handleChange}
      loadConsultancies={loadConsultancies}
      consultancies={displayConsultancies} // ✅ merged list
      consLoading={consLoading}
      toggleConnection={toggleConnection}
      updateRemark={updateRemark}
      hiringFlows={hiringFlows}
      hiringFlowsLoading={hiringFlowsLoading}
      setForm={setForm}
      currentHiringFlowName={
        post.hiringFlowName || post.hiringFlow?.hiringFlowName
      }
      currentHiringFlowId={post.hiringFlowId}
      isInterviewHistory={post.isInterviewHistory}
    />
  );

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer drawer--wide">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">Edit Job Post</h2>
            <p className="drawer__sub">
              {post.jobTitle} · #{post.jobPostCode} &nbsp;
              <span style={{ opacity: 0.6 }}>
                — Step {step + 1} of {EDIT_STEPS.length}: {EDIT_STEPS[step]}
              </span>
            </p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="drawer__stepper">
          {EDIT_STEPS.map((label, i) => (
            <div
              key={label}
              className={`drawer__step${i === step ? " drawer__step--active" : ""}${i < step ? " drawer__step--done" : ""}`}
            >
              <div className="drawer__step-dot" />
              <span>{label}</span>
            </div>
          ))}
        </div>
        <div className="drawer__body">
          {step === 0 ? stepJobDetails : stepSharing}
        </div>
        <div className="drawer__footer">
          {step > 0 ? (
            <button
              className="cv-btn cv-btn--ghost"
              onClick={() => setStep((s) => s - 1)}
            >
              ← Back
            </button>
          ) : (
            <button className="cv-btn cv-btn--ghost" onClick={onClose}>
              Cancel
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < EDIT_STEPS.length - 1 ? (
            <button
              className="cv-btn cv-btn--primary"
              onClick={() => {
                if (validate()) setStep((s) => s + 1);
              }}
            >
              Continue →
            </button>
          ) : (
            <button
              className="cv-btn cv-btn--submit"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <span className="cv-btn__spinner" /> : "Save Changes"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Shared SharingAndFlowStep
// ─────────────────────────────────────────────────────────────────────────────
const SharingAndFlowStep = ({
  form,
  errors,
  handleChange,
  loadConsultancies,
  consultancies,
  consLoading,
  toggleConnection,
  updateRemark,
  hiringFlows,
  hiringFlowsLoading,
  setForm,
  currentHiringFlowName,
  currentHiringFlowId,
  isInterviewHistory,
}) => (
  <>
    <div className="jp-section-label">Sharing Options</div>
    <div className="jp-toggles">
      <Toggle
        label="Share to Candidates Portal"
        name="shareToCandidates"
        checked={form.shareToCandidates}
        onChange={handleChange}
        icon="🌐"
      />
      <Toggle
        label="Share to Connections"
        name="shareToConsultancy"
        icon="🤝"
        checked={form.shareToConsultancy}
        onChange={(e) => {
          handleChange(e);
          if (e.target.checked) loadConsultancies();
        }}
      />
      <button
        type="button"
        className="jp-share-btn jp-share-btn--disabled"
        disabled
      >
        Post to LinkedIn<span className="jp-beta-tag">Coming Soon</span>
      </button>
      <button
        type="button"
        className="jp-share-btn jp-share-btn--disabled"
        disabled
      >
        <Icon icon="simple-icons:naukri" width={14} />
        Post to Naukri<span className="jp-beta-tag">Coming Soon</span>
      </button>
    </div>

    {form.shareToConsultancy && (
      <div className="jp-conn-panel">
        <div className="jp-conn-panel__hd">
          <span className="jp-conn-panel__title">Select Connections</span>
          {Object.keys(form.selectedConnections).length > 0 && (
            <span className="jp-conn-panel__count">
              {Object.keys(form.selectedConnections).length} selected
            </span>
          )}
        </div>
        {consLoading ? (
          <div className="jp-conn-panel__loading">
            <span className="jp-spin" /> Loading connections...
          </div>
        ) : consultancies.length === 0 ? (
          <div className="jp-conn-panel__empty">
            No active connections found.
          </div>
        ) : (
          <div className="jp-conn-list">
            {consultancies.map((con) => {
              const isSelected = Boolean(
                form.selectedConnections[String(con.connectionId)],
              );
              const id = con.connectionId;
              return (
                <React.Fragment key={id}>
                  <button
                    type="button"
                    className={`jp-conn-item${isSelected ? " jp-conn-item--selected" : ""}`}
                    onClick={() => toggleConnection(id)}
                  >
                    {con.companyLogo ? (
                      <img
                        className="jp-conn-item__logo"
                        src={`data:image/png;base64,${con.companyLogo}`}
                        alt={con.companyName}
                      />
                    ) : (
                      <div className="jp-conn-item__avatar">
                        {(con.companyName || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="jp-conn-item__info">
                      <div className="jp-conn-item__name">
                        {con.companyName || `Connection #${con.connectionId}`}
                      </div>
                      {con.moduleName && (
                        <div className="jp-conn-item__mod">
                          {con.moduleName}
                        </div>
                      )}
                    </div>
                    <div className="jp-conn-item__check">
                      {isSelected && (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M2.5 7l3 3 6-6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                  {isSelected && (
                    <div className="jp-conn-remark">
                      <input
                        className="jp-conn-remark__input"
                        placeholder="Add a remark (optional)..."
                        value={
                          form.selectedConnections[con.connectionId]?.remark ||
                          ""
                        }
                        onChange={(e) =>
                          updateRemark(con.connectionId, e.target.value)
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    )}

    <div className="jp-section-label" style={{ marginTop: 24 }}>
      Hiring Flow
    </div>

    {isInterviewHistory ? (
      <div
        style={{
          background: "#fffbeb",
          border: "1px solid #fcd34d",
          borderRadius: 8,
          padding: "12px 14px",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
            fontSize: 12,
            color: "#92400e",
            fontWeight: 600,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="6" stroke="#92400e" strokeWidth="1.4" />
            <path
              d="M7 4v3M7 9.5v.5"
              stroke="#92400e"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          Hiring flow cannot be changed — interviews have already been
          scheduled.
        </div>
        {(currentHiringFlowName || currentHiringFlowId) && (
          <div className="jp-flow-current">
            <span className="jp-flow-current__label">Current flow:</span>
            <span className="jp-flow-current__name">
              {currentHiringFlowName || `Flow #${currentHiringFlowId}`}
            </span>
          </div>
        )}
      </div>
    ) : (
      <>
        <div className="jp-flow-mode">
          <button
            type="button"
            className={`jp-flow-mode__btn${form.hiringFlowMode === "existing" ? " jp-flow-mode__btn--active" : ""}`}
            onClick={() =>
              setForm((p) => ({ ...p, hiringFlowMode: "existing" }))
            }
          >
            Use Existing Flow
          </button>
          <button
            type="button"
            className={`jp-flow-mode__btn${form.hiringFlowMode === "new" ? " jp-flow-mode__btn--active" : ""}`}
            onClick={() => setForm((p) => ({ ...p, hiringFlowMode: "new" }))}
          >
            Create New Flow
          </button>
        </div>

        {form.hiringFlowMode === "existing" ? (
          <>
            {currentHiringFlowId && (
              <div className="jp-flow-current">
                <span className="jp-flow-current__label">Current flow:</span>
                <span className="jp-flow-current__name">
                  {currentHiringFlowName || `Flow #${currentHiringFlowId}`}
                </span>
              </div>
            )}
            <F label="Select Hiring Flow" required error={errors.hiringFlowId}>
              <select
                className={`jp-input${errors.hiringFlowId ? " jp-input--error" : ""}`}
                name="hiringFlowId"
                value={form.hiringFlowId}
                onChange={handleChange}
                disabled={hiringFlowsLoading}
              >
                <option value="">
                  {hiringFlowsLoading ? "Loading..." : "Select a hiring flow"}
                </option>
                {hiringFlows.map((f) => (
                  <option key={f.hiringFlowId} value={f.hiringFlowId}>
                    {f.hiringFlowName}
                  </option>
                ))}
              </select>
            </F>
            {form.hiringFlowId &&
              (() => {
                const flow = hiringFlows.find(
                  (f) => String(f.hiringFlowId) === String(form.hiringFlowId),
                );
                const rounds =
                  flow?.rounds
                    ?.slice()
                    .sort((a, b) => a.sequence - b.sequence) || [];
                if (!rounds.length) return null;
                return (
                  <div className="jp-flow-preview">
                    <div className="jp-flow-preview__label">
                      Interview Stages
                    </div>
                    <div className="jp-flow-preview__track">
                      {rounds.map((r, idx) => (
                        <div
                          key={r.interviewRoundId || idx}
                          className="jp-flow-preview__stage"
                        >
                          <div
                            className={`jp-flow-preview__dot${r.initial ? " jp-flow-preview__dot--initial" : r.final ? " jp-flow-preview__dot--final" : ""}`}
                          >
                            {r.sequence}
                          </div>
                          <div className="jp-flow-preview__name">
                            {r.roundName}
                          </div>
                          {(r.initial || r.final) && (
                            <span
                              className={`jp-flow-preview__tag${r.final ? " jp-flow-preview__tag--final" : ""}`}
                            >
                              {r.initial ? "Initial" : "Final"}
                            </span>
                          )}
                          {idx < rounds.length - 1 && (
                            <div className="jp-flow-preview__line" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
          </>
        ) : (
          <>
            <F label="Hiring Flow Name" required error={errors.hiringFlowName}>
              <input
                className={`jp-input${errors.hiringFlowName ? " jp-input--error" : ""}`}
                name="hiringFlowName"
                value={form.hiringFlowName}
                onChange={handleChange}
                placeholder="e.g. Standard Hiring"
              />
            </F>
            <InterviewRoundsBuilder
              rounds={form.rounds}
              onChange={(rounds) => setForm((p) => ({ ...p, rounds }))}
              errors={errors}
            />
          </>
        )}
      </>
    )}
  </>
);

// ─────────────────────────────────────────────────────────────────────────────
//  CreateCompanyJobPostDrawer
// ─────────────────────────────────────────────────────────────────────────────
const STEPS_WITH_VACANCY = ["Job Details", "Sharing & Flow"];
const STEPS_WITHOUT_VACANCY = ["Vacancy Info", "Job Details", "Sharing & Flow"];

const emptyForm = {
  hasVacancy: false,
  vacancyId: "",
  jobTitle: "",
  jobDescription: "",
  responsibilities: "",
  skills: "",
  desiredProfile: "",
  experience: "",
  employmentType: "PERMANENT",
  // ✅ qualificationIds replaces qualificationType + qualification
  qualificationIds: [],
  city: "",
  state: "",
  country: "",
  office: "",
  noOfVacancy: 1,
  budget: "",
  budgetDisplay: "",
  budgetType: "annual",
  salaryType: "CTC",
  showBudget: false,
  expectedClosingTime: "",
  applicationClosingDate: "",
  roleId: "",
  roleName: "",
  departmentId: "",
  department: "",
  employeeId: "",
  hiringFlowMode: "existing",
  hiringFlowId: "",
  hiringFlowName: "",
  rounds: [
    {
      roundName: "",
      sequence: 1,
      initial: true,
      final: false,
      interviewMode: "ONLINE",
      employeeIds: [],
      description: "",
    },
  ],
  shareToCandidates: true,
  postToLinkedIn: false,
  postToNaukri: false,
  shareToConsultancy: false,
  selectedConnections: {},
  branchId: "",
};

function vacancyToForm(v) {
  return {
    hasVacancy: true,
    vacancyId: v.vacancyId,
    jobTitle: v.jobTitle || "",
    jobDescription: v.jobDescription || "",
    responsibilities: v.responsibilities || "",
    skills: v.skills || "",
    desiredProfile: v.desiredProfile || "",
    experience: v.experience || "",
    employmentType: v.employmentType || "PERMANENT",
    city: v.branchCity || v.city || "",
    state: v.branchState || v.state || "",
    country: v.branchCountry || v.country || "",
    office: v.office || v.branchName || "",
    noOfVacancy: v.noOfVacancy || 1,
    budget: String(v.budget || ""),
    budgetDisplay: v.budget ? toIndianFormat(v.budget) : "",
    budgetType: "annual",
    salaryType: "CTC",
    showBudget: v.showBudget ?? true,
    // ✅ qualificationIds from vacancy response
    qualificationIds: extractQualificationIds(v),
    expectedClosingTime: v.expectedClosingTime?.slice(0, 16) || "",
    applicationClosingDate: v.applicationClosingDate?.slice(0, 16) || "",
    roleId: v.roleId || "",
    roleName: v.roleName || "",
    departmentId: v.departmentId || "",
    department: v.departmentName || "",
    branchId: v.branchId || "",
  };
}

const CreateCompanyJobPostDrawer = ({ prefillVacancy, onClose, onCreated }) => {
  const [form, setForm] = useState(() => ({
    ...emptyForm,
    hasVacancy: !!prefillVacancy,
    ...(prefillVacancy ? vacancyToForm(prefillVacancy) : {}),
  }));
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [consultancies, setConsultancies] = useState([]);
  const [consLoading, setConsLoading] = useState(false);
  const [consLoaded, setConsLoaded] = useState(false);
  const [titleAutoFilled, setTitleAutoFilled] = useState(!prefillVacancy);

  const { roles, departments } = useRolesAndDepartments();
  const { vacancies, vacanciesLoading } = useApprovedVacancies();
  const { hiringFlows, hiringFlowsLoading } = useHiringFlows();
  const { qualifications, qualificationsLoading } = useQualifications();
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  useEffect(() => {
    setBranchesLoading(true);
    axios
      .get(BRANCHES_API)
      .then((r) => {
        const data = r.data?.data ?? r.data ?? [];
        setBranches(Array.isArray(data) ? data : []);
      })
      .catch(() => setBranches([]))
      .finally(() => setBranchesLoading(false));
  }, []);

  useEffect(() => {
    axios
      .get(`${COMMON}/employees/list`)
      .then((r) => setEmployees(r.data.data || []))
      .catch(() => {});
  }, []);

  const loadConsultancies = () => {
    if (consLoaded) return;

    setConsLoading(true);

    axios
      .get(CONNECTIONS_API)
      .then((r) => {
        const all = r.data.data || [];

        setConsultancies(
          all.filter((con) => {
            const agreementStatus = normalizeConnectionStatus(
              con.moduleAgreementStatus,
            );

            const senderStatus = normalizeConnectionStatus(
              con.senderDocumentStatus,
            );

            const receiverStatus = normalizeConnectionStatus(
              con.receiverDocumentStatus,
            );

            const isConnected =
              agreementStatus === "REQUEST_ACCEPTED" ||
              agreementStatus === "ACCEPTED";

            const tcSettled =
              senderStatus === "SENT_ACCEPTED" ||
              senderStatus === "RECEIVED_ACCEPTED" ||
              receiverStatus === "SENT_ACCEPTED" ||
              receiverStatus === "RECEIVED_ACCEPTED";

            return isConnected && tcSettled;
          }),
        );

        setConsLoaded(true);
      })
      .catch(console.error)
      .finally(() => setConsLoading(false));
  };

  const toggleConnection = (connectionId) => {
    setForm((p) => {
      const conns = { ...p.selectedConnections };
      if (conns[connectionId]) delete conns[connectionId];
      else conns[connectionId] = { connectionId, remark: "" };
      return { ...p, selectedConnections: conns };
    });
  };

  const updateRemark = (connectionId, remark) => {
    setForm((p) => ({
      ...p,
      selectedConnections: {
        ...p.selectedConnections,
        [connectionId]: { ...p.selectedConnections[connectionId], remark },
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "roleId" && titleAutoFilled) {
      const role = roles.find((r) => String(r.roleId) === value);
      setForm((p) => ({
        ...p,
        roleId: value,
        roleName: role?.roleName || "",
        jobTitle: role ? role.roleName : p.jobTitle,
      }));
      if (errors.roleId) setErrors((p) => ({ ...p, roleId: "" }));
      return;
    }
    if (name === "jobTitle") setTitleAutoFilled(false);
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleBudgetChange = (patch) => setForm((p) => ({ ...p, ...patch }));

  const handleVacancySelect = (e) => {
    const id = e.target.value;
    if (!id) {
      setForm((p) => ({ ...p, vacancyId: "" }));
      return;
    }
    const vac = vacancies.find((v) => String(v.vacancyId) === String(id));
    if (vac)
      setForm((p) => ({
        ...p,
        vacancyId: vac.vacancyId,
        ...vacancyToForm(vac),
      }));
  };

  const STEPS = form.hasVacancy ? STEPS_WITH_VACANCY : STEPS_WITHOUT_VACANCY;

  const validate = () => {
    const e = {};
    if (!form.hasVacancy) {
      if (step === 0) {
        if (!form.jobTitle.trim()) e.jobTitle = "Required";
        if (!form.noOfVacancy) e.noOfVacancy = "Required";
        if (!form.departmentId) e.departmentId = "Required";
        if (!form.roleId) e.roleId = "Required";
        if (!form.expectedClosingTime) e.expectedClosingTime = "Required";
      }
      if (step === 1 && !form.jobDescription.trim())
        e.jobDescription = "Required";
      if (step === 2) {
        if (form.hiringFlowMode === "existing" && !form.hiringFlowId)
          e.hiringFlowId = "Select a hiring flow";
        if (form.hiringFlowMode === "new") {
          if (!form.hiringFlowName.trim()) e.hiringFlowName = "Required";
          form.rounds.forEach((r, i) => {
            if (r.roundName.trim() === "" && i < form.rounds.length - 1)
              e[`round_${i}`] = "Required";
          });
        }
      }
    } else {
      if (step === 0) {
        if (!form.vacancyId) e.vacancyId = "Select a vacancy";
        if (!form.jobDescription.trim()) e.jobDescription = "Required";
      }
      if (step === 1) {
        if (form.hiringFlowMode === "existing" && !form.hiringFlowId)
          e.hiringFlowId = "Select a hiring flow";
        if (form.hiringFlowMode === "new") {
          if (!form.hiringFlowName.trim()) e.hiringFlowName = "Required";
          form.rounds.forEach((r, i) => {
            if (r.roundName.trim() === "" && i < form.rounds.length - 1)
              e[`round_${i}`] = "Required";
          });
        }
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const cleanRounds = form.rounds.filter((r) => r.roundName.trim() !== "");
      const hiringFlowPart =
        form.hiringFlowMode === "existing"
          ? { hiringFlowId: Number(form.hiringFlowId) }
          : {
              hiringFlow: {
                hiringFlowName: form.hiringFlowName,
                rounds: cleanRounds,
              },
            };

      const qualificationIds =
        form.qualificationIds.length > 0 ? form.qualificationIds : undefined;

      const payload =
        form.hasVacancy && form.vacancyId
          ? {
              vacancyId: Number(form.vacancyId),
              budget: Number(form.budget) || 0,
              budgetType: (form.budgetType || "annual").toUpperCase(),
              salaryType: form.salaryType || "CTC",
              showBudget: !!form.showBudget,
              applicationClosingDate: form.applicationClosingDate
                ? new Date(form.applicationClosingDate).toISOString()
                : undefined,
              roleName: form.roleName,
              department: form.department,
              experience: form.experience,
              // ✅ qualificationIds
              qualificationIds,
              noOfVacancy: Number(form.noOfVacancy),
              shareToCandidates: form.shareToCandidates,
              postToLinkedIn: form.postToLinkedIn,
              postToNaukri: form.postToNaukri,
              shareToConsultancy: form.shareToConsultancy,
              jobPostSharedHistoryRequestDTOS: form.shareToConsultancy
                ? Object.values(form.selectedConnections).map(
                    ({ connectionId, remark }) => ({
                      connectionId: Number(connectionId),
                      remark: remark || "",
                    }),
                  )
                : [],
              ...hiringFlowPart,
            }
          : {
              jobTitle: form.jobTitle,
              jobDescription: form.jobDescription,
              responsibilities: form.responsibilities,
              skills: form.skills,
              desiredProfile: form.desiredProfile,
              experience: form.experience,
              employmentType: normalizeEmploymentType(form.employmentType),
              // ✅ qualificationIds
              qualificationIds,
              city: form.city,
              state: form.state,
              country: form.country,
              office: form.office,
              noOfVacancy: Number(form.noOfVacancy),
              budget: Number(form.budget) || 0,
              budgetType: (form.budgetType || "annual").toUpperCase(),
              salaryType: form.salaryType || "CTC",
              showBudget: !!form.showBudget,
              expectedClosingTime: new Date(
                form.expectedClosingTime,
              ).toISOString(),
              applicationClosingDate: form.applicationClosingDate
                ? new Date(form.applicationClosingDate).toISOString()
                : undefined,
              roleId: Number(form.roleId),
              roleName: form.roleName,
              departmentId: Number(form.departmentId),
              department: form.department,
              branchId: form.branchId ? Number(form.branchId) : undefined,
              employeeId: form.employeeId ? Number(form.employeeId) : undefined,
              shareToCandidates: form.shareToCandidates,
              postToLinkedIn: form.postToLinkedIn,
              postToNaukri: form.postToNaukri,
              shareToConsultancy: form.shareToConsultancy,
              jobPostSharedHistoryRequestDTOS: form.shareToConsultancy
                ? Object.values(form.selectedConnections).map(
                    ({ connectionId, remark }) => ({
                      connectionId: Number(connectionId),
                      remark: remark || "",
                    }),
                  )
                : [],
              ...hiringFlowPart,
            };

      await axios.post(COMPANY_API, payload);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to post a job.");
    } finally {
      setLoading(false);
    }
  };

  const isPrefilled =
    !!prefillVacancy &&
    String(form.vacancyId) === String(prefillVacancy?.vacancyId);

  const vacancyOptions = useMemo(() => {
    const available = vacancies.filter((v) => {
      if (v.jobPostId) return false;
      const hrStatus = (
        v.hrapprovalStatus ||
        v.HRApprovalStatus ||
        ""
      ).toUpperCase();
      return hrStatus === "APPROVED";
    });
    if (!prefillVacancy) return available;
    return available.some((v) => v.vacancyId === prefillVacancy.vacancyId)
      ? available
      : [prefillVacancy, ...available];
  }, [vacancies, prefillVacancy]);

  const sharingStep = (
    <SharingAndFlowStep
      form={form}
      errors={errors}
      handleChange={handleChange}
      loadConsultancies={loadConsultancies}
      consultancies={consultancies}
      consLoading={consLoading}
      toggleConnection={toggleConnection}
      updateRemark={updateRemark}
      hiringFlows={hiringFlows}
      hiringFlowsLoading={hiringFlowsLoading}
      setForm={setForm}
    />
  );

  const getContent = () => {
    if (!form.hasVacancy) {
      if (step === 0)
        return (
          <VacancyInfoStep
            form={form}
            errors={errors}
            handleChange={handleChange}
            handleBudgetChange={handleBudgetChange}
            departments={departments}
            roles={roles}
            employees={employees}
            branches={branches}
            branchesLoading={branchesLoading}
            setTitleAutoFilled={setTitleAutoFilled}
            setForm={setForm}
            qualifications={qualifications}
            qualificationsLoading={qualificationsLoading}
          />
        );
      if (step === 1)
        return (
          <JobDetailsStep
            form={form}
            errors={errors}
            handleChange={handleChange}
            setForm={setForm}
            qualifications={qualifications}
            qualificationsLoading={qualificationsLoading}
          />
        );
      return sharingStep;
    } else {
      if (step === 0)
        return (
          <WithVacancyStep
            form={form}
            errors={errors}
            handleChange={handleChange}
            handleBudgetChange={handleBudgetChange}
            handleVacancySelect={handleVacancySelect}
            vacancyOptions={vacancyOptions}
            vacanciesLoading={vacanciesLoading}
            isPrefilled={isPrefilled}
            prefillVacancy={prefillVacancy}
            setForm={setForm}
            qualifications={qualifications}
            qualificationsLoading={qualificationsLoading}
          />
        );
      return sharingStep;
    }
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer drawer--wide">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">Post a Job</h2>
            <p className="drawer__sub">
              Step {step + 1} of {STEPS.length} — {STEPS[step]}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {step === 0 && (
              <>
                <span style={{ fontWeight: 600 }}>Source:</span>

                <div className="jp-mode-toggle">
                  <button
                    type="button"
                    className={`jp-mode-toggle__btn${!form.hasVacancy ? " jp-mode-toggle__btn--active" : ""}`}
                    onClick={() =>
                      setForm((p) => ({
                        ...p,
                        hasVacancy: false,
                        vacancyId: "",
                      }))
                    }
                  >
                    Manual
                  </button>

                  <button
                    type="button"
                    className={`jp-mode-toggle__btn${form.hasVacancy ? " jp-mode-toggle__btn--active" : ""}`}
                    onClick={() => setForm((p) => ({ ...p, hasVacancy: true }))}
                  >
                    Approved Vacancy
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="drawer__stepper">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className={`drawer__step${i === step ? " drawer__step--active" : ""}${i < step ? " drawer__step--done" : ""}`}
            >
              <div className="drawer__step-dot" />
              <span>{label}</span>
            </div>
          ))}
        </div>
        <div className="drawer__body">{getContent()}</div>
        <div className="drawer__footer">
          {step > 0 ? (
            <button
              className="cv-btn cv-btn--ghost"
              onClick={() => setStep((s) => s - 1)}
            >
              ← Back
            </button>
          ) : (
            <button className="cv-btn cv-btn--ghost" onClick={onClose}>
              Cancel
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < STEPS.length - 1 ? (
            <button
              className="cv-btn cv-btn--primary"
              onClick={() => {
                if (validate()) setStep((s) => s + 1);
              }}
            >
              Continue →
            </button>
          ) : (
            <button
              className="cv-btn cv-btn--submit"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <span className="cv-btn__spinner" /> : "Post a Job"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ─── VacancyInfoStep ──────────────────────────────────────────────────────────
// ✅ Qualifications multi-select passed in as props
const VacancyInfoStep = ({
  form,
  errors,
  handleChange,
  handleBudgetChange,
  departments,
  roles,
  employees,
  branches,
  branchesLoading,
  setForm,
  qualifications,
  qualificationsLoading,
}) => (
  <>
    <div className="jp-section-label">Basic Information</div>
    <div className="jp-grid-3">
      <F label="Role" required error={errors.roleId}>
        <select
          className={`jp-input${errors.roleId ? " jp-input--error" : ""}`}
          name="roleId"
          value={form.roleId}
          onChange={handleChange}
        >
          <option value="">Select Role</option>
          {roles.map((r) => (
            <option key={r.roleId} value={r.roleId}>
              {r.roleName}
            </option>
          ))}
        </select>
      </F>
      <F label="Job Title" required error={errors.jobTitle}>
        <input
          className={`jp-input${errors.jobTitle ? " jp-input--error" : ""}`}
          name="jobTitle"
          value={form.jobTitle}
          onChange={handleChange}
          placeholder="e.g. Sales Executive"
        />
      </F>
      <F label="No. of Positions" required error={errors.noOfVacancy}>
        <input
          className={`jp-input${errors.noOfVacancy ? " jp-input--error" : ""}`}
          type="number"
          min="1"
          name="noOfVacancy"
          value={form.noOfVacancy}
          onChange={handleChange}
        />
      </F>
      <F label="Employment Type">
        <select
          className="jp-input"
          name="employmentType"
          value={form.employmentType}
          onChange={handleChange}
        >
          <option value="PERMANENT">Permanent</option>
          <option value="PART_TIME">Part Time</option>
          <option value="CONTRACT">Contract</option>
          <option value="INTERN">Intern</option>
        </select>
      </F>
      <F label="Department" required error={errors.departmentId}>
        <select
          className={`jp-input${errors.departmentId ? " jp-input--error" : ""}`}
          name="departmentId"
          value={form.departmentId}
          onChange={(e) => {
            const d = departments.find(
              (x) => String(x.departmentId) === e.target.value,
            );
            setForm((p) => ({
              ...p,
              departmentId: e.target.value,
              department: d?.departmentName || "",
            }));
          }}
        >
          <option value="">Select Department</option>
          {departments.map((d) => (
            <option key={d.departmentId} value={d.departmentId}>
              {d.departmentName}
            </option>
          ))}
        </select>
      </F>
      <F label="Branch" error={errors.branchId}>
        <select
          className={`jp-input${errors.branchId ? " jp-input--error" : ""}`}
          name="branchId"
          value={form.branchId}
          onChange={(e) => setForm((p) => ({ ...p, branchId: e.target.value }))}
          disabled={branchesLoading}
        >
          <option value="">
            {branchesLoading ? "Loading branches…" : "Select Branch"}
          </option>
          {branches.map((b) => (
            <option key={b.branchId} value={b.branchId}>
              {b.branchName}
              {b.branchAddress
                ? ` — ${b.branchAddress.city || ""}, ${b.branchAddress.state || ""}`
                : ""}
            </option>
          ))}
        </select>
      </F>
    </div>
    <div className="jp-grid-1">
      <F label="Budget">
        <BudgetInput
          value={form.budget}
          display={form.budgetDisplay}
          budgetType={form.budgetType}
          salaryType={form.salaryType}
          onChange={handleBudgetChange}
        />
      </F>
      <Toggle
        label="Show budget to candidates"
        name="showBudget"
        checked={form.showBudget}
        onChange={handleChange}
        icon="💰"
      />
    </div>
    <div className="jp-grid-2">
      <F
        label="Expected Closing Date"
        required
        error={errors.expectedClosingTime}
      >
        <DateTimePicker
          name="expectedClosingTime"
          value={form.expectedClosingTime}
          onChange={handleChange}
          error={errors.expectedClosingTime}
        />
      </F>
      <F label="Application Closing Date">
        <DateTimePicker
          name="applicationClosingDate"
          value={form.applicationClosingDate}
          onChange={handleChange}
        />
      </F>
    </div>
    <div className="jp-grid-1">
      <F label="Experience">
        <ExperienceCards value={form.experience} onChange={handleChange} />
      </F>
    </div>
  </>
);

// ─── JobDetailsStep ───────────────────────────────────────────────────────────
// ✅ Qualifications multi-select replaces the old two dropdowns
const JobDetailsStep = ({
  form,
  errors,
  handleChange,
  setForm,
  qualifications,
  qualificationsLoading,
}) => (
  <>
    <div className="jp-section-label">Job Content</div>
    <F label="Job Description" required error={errors.jobDescription}>
      <textarea
        className={`jp-input jp-input--textarea${errors.jobDescription ? " jp-input--error" : ""}`}
        name="jobDescription"
        value={form.jobDescription}
        onChange={handleChange}
        rows={4}
        placeholder="Describe the role..."
      />
      <div className="suggestion-chips" style={{ marginTop: 6 }}>
        {JOB_DESCRIPTION_SUGGESTIONS.map((s) => (
          <button
            type="button"
            key={s}
            className="suggestion-chip"
            onClick={() =>
              setForm((p) => ({
                ...p,
                jobDescription: p.jobDescription
                  ? `${p.jobDescription}\n${s}`
                  : s,
              }))
            }
          >
            + {s}
          </button>
        ))}
      </div>
    </F>
    <div style={{ marginTop: 16 }}>
      <F label="Responsibilities">
        <textarea
          className="jp-input jp-input--textarea"
          name="responsibilities"
          value={form.responsibilities}
          onChange={handleChange}
          rows={3}
          placeholder="Key responsibilities..."
        />
        <div className="suggestion-chips" style={{ marginTop: 6 }}>
          {RESPONSIBILITY_SUGGESTIONS.map((s) => (
            <button
              type="button"
              key={s}
              className="suggestion-chip"
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  responsibilities: p.responsibilities
                    ? `${p.responsibilities}\n• ${s}`
                    : `• ${s}`,
                }))
              }
            >
              + {s}
            </button>
          ))}
        </div>
      </F>
    </div>
    <div style={{ marginTop: 16 }}>
      <F label="Desired Profile">
        <textarea
          className="jp-input jp-input--textarea"
          name="desiredProfile"
          value={form.desiredProfile}
          onChange={handleChange}
          rows={3}
          placeholder="Ideal candidate profile..."
        />
        <div className="suggestion-chips" style={{ marginTop: 6 }}>
          {DESIRED_PROFILE_SUGGESTIONS.map((s) => (
            <button
              type="button"
              key={s}
              className="suggestion-chip"
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  desiredProfile: p.desiredProfile
                    ? `${p.desiredProfile}\n• ${s}`
                    : `• ${s}`,
                }))
              }
            >
              + {s}
            </button>
          ))}
        </div>
      </F>
    </div>
    <div className="jp-grid-2" style={{ marginTop: 16 }}>
      <F label="Skills" hint="Comma-separated">
        <input
          className="jp-input"
          name="skills"
          value={form.skills}
          onChange={handleChange}
          placeholder="e.g. Communication, Sales, Excel"
        />
      </F>
    </div>
    <F label="Qualifications" style={{ marginTop: 16 }}>
      <QualificationMultiSelect
        qualifications={qualifications}
        qualificationsLoading={qualificationsLoading}
        selectedIds={form.qualificationIds}
        onChange={(ids) => setForm((p) => ({ ...p, qualificationIds: ids }))}
      />
    </F>
  </>
);

// ─── WithVacancyStep ──────────────────────────────────────────────────────────
const WithVacancyStep = ({
  form,
  errors,
  handleChange,
  handleBudgetChange,
  handleVacancySelect,
  vacancyOptions,
  vacanciesLoading,
  isPrefilled,
  setForm,
  qualifications,
  qualificationsLoading,
}) => {
  if (!vacanciesLoading && vacancyOptions.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 24px",
          textAlign: "center",
          gap: 12,
        }}
      >
        <span style={{ fontSize: 40 }}>📋</span>
        <div style={{ fontWeight: 600, fontSize: 15, color: "#111827" }}>
          No vacancies available
        </div>
        <p
          style={{
            fontSize: 13,
            color: "#6b7280",
            maxWidth: 340,
            margin: 0,
            lineHeight: 1.6,
          }}
        >
          All approved vacancies already have an associated job post, or no
          vacancies have been approved yet. Switch to{" "}
          <strong>No Vacancy</strong> to create a job post manually.
        </p>
      </div>
    );
  }

  return (
    <>
      <F
        label="Approved Vacancy"
        required
        error={errors.vacancyId}
        hint={
          isPrefilled
            ? "Pre-selected from HR Approvals"
            : "Selecting a vacancy auto-fills all job details"
        }
      >
        <select
          className={`jp-input${errors.vacancyId ? " jp-input--error" : ""}`}
          name="vacancyId"
          value={form.vacancyId}
          onChange={handleVacancySelect}
          disabled={vacanciesLoading && !form.vacancyId}
        >
          <option value="">
            {vacanciesLoading ? "Loading…" : "Select Approved Vacancy"}
          </option>
          {vacancyOptions.map((v) => (
            <option key={v.vacancyId} value={v.vacancyId}>
              {v.jobTitle} ({v.departmentName || `Dept ${v.departmentId}`})
            </option>
          ))}
        </select>
      </F>

      {form.vacancyId && (
        <>
          {isPrefilled && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 8,
                padding: "8px 12px",
                marginTop: 10,
                fontSize: 12,
                color: "#16a34a",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7l3 3 7-7"
                  stroke="#16a34a"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              All fields pre-filled from the approved vacancy.
            </div>
          )}

          <div className="jp-vacancy-chip">
            <div className="jp-vacancy-chip__title">{form.jobTitle}</div>
            <div className="jp-vacancy-chip__meta">
              {form.department && <span>🏢 {form.department}</span>}
              {form.roleName && <span>🎯 {form.roleName}</span>}
              {form.noOfVacancy > 0 && (
                <span>
                  👥 {form.noOfVacancy} position
                  {form.noOfVacancy !== 1 ? "s" : ""}
                </span>
              )}
              {form.city && <span>📍 {form.city}</span>}
            </div>
          </div>

          <div className="jp-section-label" style={{ marginTop: 20 }}>
            Review Job Details
          </div>

          <div className="jp-grid-2">
            <F label="No. of Positions">
              <input
                className="jp-input"
                type="number"
                min="1"
                name="noOfVacancy"
                value={form.noOfVacancy}
                onChange={handleChange}
              />
            </F>
            <F label="Experience">
              <>
                <input
                  className="jp-input"
                  name="experience"
                  value={form.experience}
                  onChange={handleChange}
                  placeholder="e.g. 3-5 years"
                />
                <div className="suggestion-chips">
                  {EXPERIENCE_SUGGESTIONS.map((item) => (
                    <button
                      type="button"
                      key={item}
                      className="suggestion-chip"
                      onClick={() =>
                        setForm((p) => ({ ...p, experience: item }))
                      }
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </>
            </F>
          </div>

          <div className="jp-grid-1">
            <F label="Budget">
              <BudgetInput
                value={form.budget}
                display={form.budgetDisplay}
                budgetType={form.budgetType}
                salaryType={form.salaryType}
                onChange={handleBudgetChange}
              />
            </F>
            <Toggle
              label="Show budget to candidates"
              name="showBudget"
              checked={form.showBudget}
              onChange={handleChange}
              icon="💰"
            />
          </div>

          <div className="jp-grid-1">
            <F label="Application Closing Date">
              <DateTimePicker
                name="applicationClosingDate"
                value={form.applicationClosingDate}
                onChange={handleChange}
              />
            </F>
          </div>

          <F label="Job Description" required error={errors.jobDescription}>
            <textarea
              className={`jp-input jp-input--textarea${
                errors.jobDescription ? " jp-input--error" : ""
              }`}
              name="jobDescription"
              value={form.jobDescription}
              onChange={handleChange}
              rows={4}
            />
            <div className="suggestion-chips" style={{ marginTop: 6 }}>
              {JOB_DESCRIPTION_SUGGESTIONS.map((s) => (
                <button
                  type="button"
                  key={s}
                  className="suggestion-chip"
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      jobDescription: p.jobDescription
                        ? `${p.jobDescription}\n${s}`
                        : s,
                    }))
                  }
                >
                  + {s}
                </button>
              ))}
            </div>
          </F>

          <div style={{ marginTop: 14 }}>
            <F label="Responsibilities">
              <>
                <textarea
                  className="jp-input jp-input--textarea"
                  name="responsibilities"
                  value={form.responsibilities}
                  onChange={handleChange}
                  rows={3}
                />
                <div className="suggestion-chips">
                  {RESPONSIBILITY_SUGGESTIONS.map((item) => (
                    <button
                      type="button"
                      key={item}
                      className="suggestion-chip"
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          responsibilities: p.responsibilities
                            ? `${p.responsibilities}\n• ${item}`
                            : `• ${item}`,
                        }))
                      }
                    >
                      + {item}
                    </button>
                  ))}
                </div>
              </>
            </F>
          </div>

          <div style={{ marginTop: 14 }}>
            <F label="Desired Profile">
              <>
                <textarea
                  className="jp-input jp-input--textarea"
                  name="desiredProfile"
                  value={form.desiredProfile}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Ideal candidate profile..."
                />
                <div className="suggestion-chips" style={{ marginTop: 6 }}>
                  {DESIRED_PROFILE_SUGGESTIONS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      className="suggestion-chip"
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          desiredProfile: p.desiredProfile
                            ? `${p.desiredProfile}\n• ${s}`
                            : `• ${s}`,
                        }))
                      }
                    >
                      + {s}
                    </button>
                  ))}
                </div>
              </>
            </F>
          </div>

          <div style={{ marginTop: 14 }}>
            <F label="Skills" hint="Comma-separated">
              <input
                className="jp-input"
                name="skills"
                value={form.skills}
                onChange={handleChange}
              />
            </F>
          </div>

          {/* ✅ qualificationIds multi-select replaces old two-dropdown */}
          <F label="Qualifications" style={{ marginTop: 14 }}>
            <QualificationMultiSelect
              qualifications={qualifications}
              qualificationsLoading={qualificationsLoading}
              selectedIds={form.qualificationIds}
              onChange={(ids) =>
                setForm((p) => ({ ...p, qualificationIds: ids }))
              }
            />
          </F>
        </>
      )}
    </>
  );
};

export default AllJobPosts;
